import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth, API } from "../App";
import { toast } from "sonner";
import Sidebar from "../components/Sidebar";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ScrollArea } from "../components/ui/scroll-area";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import {
  Mail, Plus, Edit, Trash2, Loader2, Copy, Eye, FileText,
  Search, Code, Variable, Save, RefreshCw, AlertTriangle,
  CheckCircle, X, Clipboard, ChevronRight, Send, Users, Check, Settings, Image, Upload, Zap, ChevronDown, ChevronUp, LayoutGrid, List, Paintbrush
} from "lucide-react";
import { Checkbox } from "../components/ui/checkbox";
import VisualEmailEditor from "./VisualEmailEditor";

// Campos disponibles por módulo
const CAMPOS_SISTEMA = {
  nuevo_usuario: {
    label: "Nuevo Usuario (Creación)",
    campos: [
      { id: "nuevo_usuario.name", label: "Nombre del nuevo usuario", ejemplo: "Juan Pérez" },
      { id: "nuevo_usuario.email", label: "Email del nuevo usuario", ejemplo: "juan@empresa.com" },
      { id: "nuevo_usuario.password", label: "Contraseña asignada", ejemplo: "Password123" },
      { id: "nuevo_usuario.role", label: "Rol asignado", ejemplo: "Analista" },
      { id: "nuevo_usuario.departamento", label: "Departamento", ejemplo: "Sistemas" },
      { id: "nuevo_usuario.cargo", label: "Cargo", ejemplo: "Analista de Sistemas" },
      { id: "nuevo_usuario.telefono", label: "Teléfono", ejemplo: "809-555-1234" },
    ]
  },
  usuario_afectado: {
    label: "Usuario Afectado (Edición/Inhabilitación)",
    campos: [
      { id: "usuario_afectado.name", label: "Nombre del usuario afectado", ejemplo: "María García" },
      { id: "usuario_afectado.email", label: "Email del usuario afectado", ejemplo: "maria@empresa.com" },
      { id: "usuario_afectado.role", label: "Rol del usuario", ejemplo: "Usuario" },
      { id: "nueva_password", label: "Nueva contraseña (Reset)", ejemplo: "NuevaPass123" },
    ]
  },
  usuario: {
    label: "Usuario Logueado",
    campos: [
      { id: "user.name", label: "Nombre del usuario logueado", ejemplo: "Juan Pérez" },
      { id: "user.email", label: "Correo del usuario logueado", ejemplo: "juan@empresa.com" },
      { id: "user.role", label: "Rol", ejemplo: "Administrador" },
      { id: "user.departamento", label: "Departamento", ejemplo: "Sistemas" },
      { id: "user.cargo", label: "Cargo", ejemplo: "Gerente de TI" },
      { id: "user.telefono", label: "Teléfono", ejemplo: "809-555-1234" },
    ]
  },
  ticket: {
    label: "Mesa de Ayuda",
    campos: [
      { id: "ticket.id", label: "ID del ticket", ejemplo: "TKT-2024-001" },
      { id: "ticket.titulo", label: "Título", ejemplo: "Error en sistema" },
      { id: "ticket.descripcion", label: "Descripción", ejemplo: "El sistema presenta errores..." },
      { id: "ticket.estado", label: "Estado", ejemplo: "En Proceso" },
      { id: "ticket.prioridad", label: "Prioridad", ejemplo: "Alta" },
      { id: "ticket.categoria", label: "Categoría", ejemplo: "Soporte Técnico" },
      { id: "ticket.creador", label: "Creado por", ejemplo: "María García" },
      { id: "ticket.asignado", label: "Asignado a", ejemplo: "Carlos Rodríguez" },
      { id: "ticket.fecha_creacion", label: "Fecha de creación", ejemplo: "15/02/2026" },
      { id: "ticket.fecha_actualizacion", label: "Última actualización", ejemplo: "16/02/2026" },
    ]
  },
  requisicion: {
    label: "Requisiciones",
    campos: [
      { id: "requisicion.id", label: "ID de requisición", ejemplo: "REQ-2024-001" },
      { id: "requisicion.titulo", label: "Título", ejemplo: "Materiales de oficina" },
      { id: "requisicion.descripcion", label: "Descripción", ejemplo: "Se requieren materiales..." },
      { id: "requisicion.estado", label: "Estado", ejemplo: "Pendiente" },
      { id: "requisicion.solicitante", label: "Solicitante", ejemplo: "Ana López" },
      { id: "requisicion.departamento", label: "Departamento", ejemplo: "Administración" },
      { id: "requisicion.fecha_solicitud", label: "Fecha de solicitud", ejemplo: "15/02/2026" },
      { id: "requisicion.total", label: "Total estimado", ejemplo: "RD$5,000.00" },
    ]
  },
  solicitud_gh: {
    label: "Gestión Humana",
    campos: [
      { id: "solicitud.id", label: "ID de solicitud", ejemplo: "SOL-2024-001" },
      { id: "solicitud.tipo", label: "Tipo", ejemplo: "Permiso" },
      { id: "solicitud.empleado", label: "Empleado", ejemplo: "Pedro Martínez" },
      { id: "solicitud.departamento", label: "Departamento", ejemplo: "Ventas" },
      { id: "solicitud.supervisor", label: "Supervisor", ejemplo: "Laura Sánchez" },
      { id: "solicitud.fecha_inicio", label: "Fecha inicio", ejemplo: "20/02/2026" },
      { id: "solicitud.fecha_fin", label: "Fecha fin", ejemplo: "21/02/2026" },
      { id: "solicitud.motivo", label: "Motivo", ejemplo: "Cita médica" },
      { id: "solicitud.estado", label: "Estado", ejemplo: "Aprobada" },
      { id: "solicitud_id", label: "ID de la solicitud (sin punto)", ejemplo: "SOL-001" },
      { id: "dias_solicitados", label: "Días solicitados", ejemplo: "2" },
      { id: "dias_restantes", label: "Días restantes después de la solicitud", ejemplo: "3" },
      { id: "aprobado_por", label: "Nombre de quien aprobó", ejemplo: "María García" },
      { id: "rechazado_por", label: "Nombre de quien rechazó", ejemplo: "Carlos Rodríguez" },
      { id: "completado_por", label: "Nombre de quien marcó como completada", ejemplo: "Ana López" },
      { id: "creado_por", label: "Creado por", ejemplo: "Admin Test" },
      { id: "motivo_rechazo", label: "Motivo de rechazo", ejemplo: "Documentación incompleta" },
      { id: "comentarios", label: "Comentarios del aprobador", ejemplo: "OK aprobado" },
      { id: "supervisor", label: "Nombre supervisor", ejemplo: "Laura Sánchez" },
      { id: "departamento", label: "Departamento", ejemplo: "Sistemas" },
    ]
  },
  permisos_ausencias: {
    label: "Permisos / Ausencias / Tardanzas",
    campos: [
      { id: "tipo_permiso", label: "Tipo de permiso", ejemplo: "Médico" },
      { id: "fecha_inicio", label: "Fecha de inicio", ejemplo: "20/02/2026" },
      { id: "fecha_fin", label: "Fecha de fin", ejemplo: "21/02/2026" },
      { id: "hora_inicio", label: "Hora de inicio", ejemplo: "08:00" },
      { id: "hora_fin", label: "Hora de fin", ejemplo: "12:00" },
      { id: "hora_entrada", label: "Hora de entrada (tardanza)", ejemplo: "09:15" },
      { id: "hora_salida", label: "Hora de salida", ejemplo: "17:30" },
      { id: "horas", label: "Cantidad de horas", ejemplo: "4" },
      { id: "dias", label: "Cantidad de días", ejemplo: "2" },
      { id: "motivo", label: "Motivo", ejemplo: "Cita médica" },
      { id: "fecha", label: "Fecha del evento", ejemplo: "20/02/2026" },
      { id: "hora", label: "Hora del evento", ejemplo: "10:30" },
    ]
  },
  cumpleanos: {
    label: "Día de Cumpleaños",
    campos: [
      { id: "fecha_solicitada", label: "Fecha del día libre que el empleado eligió", ejemplo: "15/03/2026" },
      { id: "fecha_cumpleanos", label: "Fecha del cumpleaños del empleado", ejemplo: "15/03/2026" },
      { id: "aprobado_por", label: "Aprobado por", ejemplo: "María García" },
      { id: "rechazado_por", label: "Rechazado por", ejemplo: "Carlos Rodríguez" },
      { id: "motivo_rechazo", label: "Motivo de rechazo", ejemplo: "Fecha no disponible" },
      { id: "nombre", label: "Nombre del empleado", ejemplo: "Juan Pérez" },
    ]
  },
  saldo_dias: {
    label: "Saldo de Días (Vacaciones/Permisos)",
    campos: [
      { id: "saldo.vacaciones_totales", label: "Días totales de vacaciones", ejemplo: "14" },
      { id: "saldo.vacaciones_usados", label: "Días de vacaciones usados", ejemplo: "5" },
      { id: "saldo.vacaciones_disponibles", label: "Días de vacaciones disponibles", ejemplo: "9" },
      { id: "saldo.permisos_maximos", label: "Días máximos de permisos", ejemplo: "6" },
      { id: "saldo.permisos_usados", label: "Días de permisos usados", ejemplo: "2" },
      { id: "saldo.permisos_disponibles", label: "Días de permisos disponibles", ejemplo: "4" },
      { id: "umbral.dias_configurado", label: "Umbral configurado para alerta", ejemplo: "5" },
    ]
  },
  firmas: {
    label: "Firmas Digitales",
    campos: [
      { id: "firma.empleado_url", label: "Imagen de firma del empleado", ejemplo: "<img src='...' />" },
      { id: "firma.empleado_nombre", label: "Nombre del empleado que firma", ejemplo: "Juan Pérez" },
      { id: "firma.empleado_cargo", label: "Cargo del empleado", ejemplo: "Analista" },
      { id: "firma.supervisor_url", label: "Imagen de firma del supervisor", ejemplo: "<img src='...' />" },
      { id: "firma.supervisor_nombre", label: "Nombre del supervisor que aprueba", ejemplo: "María García" },
      { id: "firma.supervisor_cargo", label: "Cargo del supervisor", ejemplo: "Gerente" },
      { id: "firma.aprobador_url", label: "Imagen de firma del aprobador final", ejemplo: "<img src='...' />" },
      { id: "firma.aprobador_nombre", label: "Nombre del aprobador final", ejemplo: "Carlos Rodríguez" },
      { id: "firma.aprobador_cargo", label: "Cargo del aprobador", ejemplo: "Director GH" },
      { id: "firma.fecha_firma_empleado", label: "Fecha de firma del empleado", ejemplo: "15/02/2026" },
      { id: "firma.fecha_firma_supervisor", label: "Fecha de firma del supervisor", ejemplo: "16/02/2026" },
    ]
  },
  visitante: {
    label: "Control de Visitantes",
    campos: [
      { id: "visitante.nombre", label: "Nombre", ejemplo: "José García" },
      { id: "visitante.cedula", label: "Cédula/ID", ejemplo: "001-1234567-8" },
      { id: "visitante.empresa", label: "Empresa", ejemplo: "Consultores SA" },
      { id: "visitante.motivo", label: "Motivo de visita", ejemplo: "Reunión de trabajo" },
      { id: "visitante.persona_visita", label: "Persona a visitar", ejemplo: "María López" },
      { id: "visitante.departamento", label: "Departamento", ejemplo: "Gerencia" },
      { id: "visitante.fecha_entrada", label: "Fecha/Hora entrada", ejemplo: "15/02/2026 09:30" },
      { id: "visitante.fecha_salida", label: "Fecha/Hora salida", ejemplo: "15/02/2026 11:45" },
    ]
  },
  factura: {
    label: "Cuentas por Pagar",
    campos: [
      { id: "factura.numero", label: "Número de factura", ejemplo: "FAC-001234" },
      { id: "factura.proveedor", label: "Proveedor", ejemplo: "Suministros XYZ" },
      { id: "factura.monto", label: "Monto", ejemplo: "RD$25,000.00" },
      { id: "factura.fecha_emision", label: "Fecha de emisión", ejemplo: "10/02/2026" },
      { id: "factura.fecha_vencimiento", label: "Fecha de vencimiento", ejemplo: "10/03/2026" },
      { id: "factura.estado", label: "Estado", ejemplo: "Pendiente" },
      { id: "factura.concepto", label: "Concepto", ejemplo: "Suministros de oficina" },
    ]
  },
  mensajeria: {
    label: "Mensajería",
    campos: [
      { id: "solicitud_id", label: "ID de la solicitud", ejemplo: "MSG-2026-001" },
      { id: "numero", label: "Número de solicitud", ejemplo: "1234" },
      { id: "cliente", label: "Cliente", ejemplo: "Empresa ABC" },
      { id: "destinatario", label: "Destinatario", ejemplo: "Juan Pérez" },
      { id: "direccion", label: "Dirección de entrega", ejemplo: "Calle Principal #123" },
      { id: "telefono", label: "Teléfono de contacto", ejemplo: "809-555-1234" },
      { id: "descripcion", label: "Descripción del envío", ejemplo: "Documentos importantes" },
      { id: "es_urgente", label: "¿Es urgente?", ejemplo: "Sí" },
      { id: "estado", label: "Estado de la solicitud", ejemplo: "En Proceso" },
      { id: "mensajero", label: "Mensajero asignado", ejemplo: "Carlos Rodríguez" },
      { id: "fecha_creacion", label: "Fecha de creación", ejemplo: "15/02/2026" },
      { id: "fecha_entrega", label: "Fecha de entrega", ejemplo: "16/02/2026" },
      { id: "fecha_devolucion", label: "Fecha de devolución", ejemplo: "16/02/2026" },
      { id: "devuelto_por", label: "Devuelto por", ejemplo: "Carlos Rodríguez" },
      { id: "motivo_devolucion", label: "Motivo de devolución", ejemplo: "Dirección no encontrada" },
    ]
  },
  inventario: {
    label: "Inventario",
    campos: [
      { id: "articulo", label: "Nombre del artículo", ejemplo: "Resma de papel A4" },
      { id: "articulo_id", label: "ID del artículo", ejemplo: "ART-001" },
      { id: "codigo", label: "Código interno", ejemplo: "PAP-001" },
      { id: "stock_actual", label: "Stock actual", ejemplo: "5" },
      { id: "stock_minimo", label: "Stock mínimo configurado", ejemplo: "10" },
      { id: "cantidad", label: "Cantidad", ejemplo: "20" },
      { id: "ubicacion", label: "Ubicación", ejemplo: "Almacén central · Estante A" },
      { id: "categoria", label: "Categoría", ejemplo: "Papelería" },
      { id: "items_count", label: "Cantidad de ítems afectados", ejemplo: "3" },
      { id: "lista_articulos", label: "Lista HTML de artículos", ejemplo: "(generado automáticamente)" },
      { id: "lista_entregados", label: "Lista de artículos entregados", ejemplo: "(generado)" },
      { id: "lista_pendientes", label: "Lista de artículos pendientes", ejemplo: "(generado)" },
      { id: "entregado_por", label: "Entregado por", ejemplo: "Juan Pérez" },
      { id: "destino", label: "Departamento/persona destino", ejemplo: "Sistemas" },
      { id: "fecha_entrega", label: "Fecha de entrega", ejemplo: "15/02/2026" },
    ]
  },
  riesgos_personas: {
    label: "Riesgos de Personas",
    campos: [
      { id: "asegurado.id", label: "ID del asegurado", ejemplo: "ASG-001" },
      { id: "asegurado.nombre", label: "Nombre del asegurado", ejemplo: "Juan Pérez" },
      { id: "asegurado.certificado", label: "Número de certificado", ejemplo: "CERT-2026-001" },
      { id: "asegurado.fecha_inicio", label: "Fecha de inicio de cobertura", ejemplo: "01/01/2026" },
      { id: "certificado.id", label: "ID del certificado", ejemplo: "CERT-001" },
      { id: "certificado.numero", label: "Número de certificado", ejemplo: "C-2026-1234" },
      { id: "certificado.fecha_inicio", label: "Fecha inicio cobertura", ejemplo: "01/01/2026" },
      { id: "certificado.fecha_fin", label: "Fecha fin cobertura", ejemplo: "31/12/2026" },
      { id: "certificado.num_dependientes", label: "N° de dependientes", ejemplo: "3" },
      { id: "plan.id", label: "ID del plan", ejemplo: "PLN-001" },
      { id: "plan.nombre", label: "Nombre del plan", ejemplo: "Plan Familiar Plus" },
      { id: "plan.aseguradora", label: "Aseguradora", ejemplo: "Mapfre BHD" },
      { id: "plan.tipo_cobertura", label: "Tipo de cobertura", ejemplo: "Salud" },
      { id: "plan.fecha_vencimiento", label: "Fecha de vencimiento", ejemplo: "31/12/2026" },
      { id: "plan.dias_restantes", label: "Días restantes para vencer", ejemplo: "15" },
      { id: "plan.prima_renovacion", label: "Prima de renovación", ejemplo: "RD$25,000" },
      { id: "reclamacion.id", label: "ID de reclamación", ejemplo: "RCL-001" },
      { id: "reclamacion.numero", label: "Número de reclamación", ejemplo: "R-2026-0123" },
      { id: "reclamacion.tipo", label: "Tipo de reclamación", ejemplo: "Consulta médica" },
      { id: "reclamacion.proveedor", label: "Proveedor de servicio", ejemplo: "Hospital General" },
      { id: "reclamacion.fecha_servicio", label: "Fecha del servicio", ejemplo: "10/02/2026" },
      { id: "reclamacion.monto", label: "Monto reclamado", ejemplo: "RD$5,000" },
      { id: "reclamacion.monto_reclamado", label: "Monto reclamado", ejemplo: "RD$5,000" },
      { id: "reclamacion.monto_aprobado", label: "Monto aprobado", ejemplo: "RD$4,500" },
      { id: "reclamacion.cobertura_porcentaje", label: "% de cobertura", ejemplo: "90%" },
      { id: "reclamacion.motivo_rechazo", label: "Motivo de rechazo", ejemplo: "Sin cobertura" },
    ]
  },
  riesgos_generales: {
    label: "Riesgos Generales",
    campos: [
      { id: "poliza.id", label: "ID de póliza", ejemplo: "POL-001" },
      { id: "poliza.numero", label: "Número de póliza", ejemplo: "P-2026-0123" },
      { id: "poliza.tipo", label: "Tipo de póliza", ejemplo: "Vehículo" },
      { id: "poliza.aseguradora", label: "Aseguradora", ejemplo: "Universal" },
      { id: "poliza.prima", label: "Prima", ejemplo: "RD$30,000" },
      { id: "poliza.monto_prima", label: "Monto de la prima", ejemplo: "RD$30,000" },
      { id: "poliza.fecha_vencimiento", label: "Fecha de vencimiento", ejemplo: "31/12/2026" },
      { id: "poliza.fecha_limite_pago", label: "Fecha límite de pago", ejemplo: "15/12/2026" },
      { id: "poliza.dias_restantes", label: "Días restantes para vencer", ejemplo: "15" },
      { id: "poliza.dias_vencida", label: "Días vencida", ejemplo: "3" },
      { id: "cotizacion.id", label: "ID de cotización", ejemplo: "COT-001" },
      { id: "cotizacion.numero", label: "Número de cotización", ejemplo: "C-2026-001" },
      { id: "cotizacion.tipo", label: "Tipo de cotización", ejemplo: "Auto" },
      { id: "cotizacion.aseguradora", label: "Aseguradora", ejemplo: "Sura" },
      { id: "cotizacion.cobertura", label: "Cobertura", ejemplo: "Full" },
      { id: "cotizacion.prima_anual", label: "Prima anual", ejemplo: "RD$45,000" },
      { id: "cotizacion.fecha_vigencia", label: "Fecha de vigencia", ejemplo: "31/12/2026" },
      { id: "siniestro.id", label: "ID del siniestro", ejemplo: "SIN-001" },
      { id: "siniestro.numero", label: "Número del siniestro", ejemplo: "S-2026-001" },
      { id: "siniestro.tipo", label: "Tipo de siniestro", ejemplo: "Colisión" },
      { id: "siniestro.fecha", label: "Fecha del siniestro", ejemplo: "10/02/2026" },
      { id: "siniestro.descripcion", label: "Descripción del siniestro", ejemplo: "Colisión trasera" },
      { id: "siniestro.poliza", label: "Póliza asociada", ejemplo: "P-2026-001" },
      { id: "siniestro.monto_aprobado", label: "Monto aprobado", ejemplo: "RD$50,000" },
      { id: "siniestro.metodo_pago", label: "Método de pago", ejemplo: "Transferencia" },
      { id: "metodo_pago", label: "Método de pago genérico", ejemplo: "Transferencia bancaria" },
      { id: "referencia", label: "Referencia bancaria", ejemplo: "REF-001234" },
      { id: "monto", label: "Monto", ejemplo: "RD$25,000" },
    ]
  },
  reportes_errores: {
    label: "Reportes de Problemas",
    campos: [
      { id: "reporte.asunto", label: "Asunto del reporte", ejemplo: "Error al guardar" },
      { id: "reporte.descripcion", label: "Descripción detallada", ejemplo: "Al hacer clic..." },
      { id: "reporte.severidad", label: "Severidad", ejemplo: "Alta" },
      { id: "reporte.modulo", label: "Módulo afectado", ejemplo: "Mensajería" },
      { id: "reporte.fecha", label: "Fecha de creación", ejemplo: "15/02/2026" },
      { id: "reporte.pasos", label: "Pasos para reproducir", ejemplo: "1. Click..." },
      { id: "reporte.usuario", label: "Usuario que reportó", ejemplo: "Juan Pérez" },
      { id: "reporte.usuario_email", label: "Email del usuario que reportó", ejemplo: "juan@empresa.com" },
      { id: "reporte.url", label: "URL al reporte", ejemplo: "https://.../reportes/123" },
      { id: "reporte.url_admin", label: "URL al admin del reporte", ejemplo: "https://.../admin/reportes/123" },
      { id: "reporte.notas_admin", label: "Notas del administrador", ejemplo: "Resuelto en v2.1" },
    ]
  },
  correo_interno: {
    label: "Mensajes / Correspondencia Interna",
    campos: [
      { id: "destinatario_nombre", label: "Nombre del destinatario", ejemplo: "Ana López" },
      { id: "remitente", label: "Remitente", ejemplo: "Juan Pérez" },
      { id: "mensaje", label: "Mensaje", ejemplo: "Hola, te comparto..." },
      { id: "comentario", label: "Comentario", ejemplo: "Aprobado con observaciones" },
      { id: "comentario_autor", label: "Autor del comentario", ejemplo: "María García" },
      { id: "asunto", label: "Asunto", ejemplo: "Aprobación pendiente" },
      { id: "registrado_por", label: "Registrado por", ejemplo: "Admin Sistema" },
      { id: "restablecido_por", label: "Restablecido por", ejemplo: "Admin Sistema" },
      { id: "solicitante_nombre", label: "Nombre del solicitante", ejemplo: "Pedro Martínez" },
      { id: "solicitante", label: "Solicitante", ejemplo: "Pedro Martínez" },
      { id: "usuario_nombre", label: "Nombre de usuario", ejemplo: "pedro.martinez" },
    ]
  },
  visitantes: {
    label: "Visitantes / Recepción",
    campos: [
      { id: "visitante", label: "Nombre del visitante", ejemplo: "José García" },
      { id: "visitante_id", label: "ID del visitante", ejemplo: "VIS-001" },
      { id: "visita_a", label: "Persona a quien visita", ejemplo: "María López" },
      { id: "empresa", label: "Empresa del visitante", ejemplo: "Consultores SA" },
      { id: "cedula", label: "Cédula del visitante", ejemplo: "001-1234567-8" },
      { id: "tiempo_permanencia", label: "Tiempo de permanencia", ejemplo: "2 horas 15 min" },
    ]
  },
  cuentas_pagar: {
    label: "Cuentas por Pagar (Variables flat)",
    campos: [
      { id: "factura", label: "Número/identificador de factura", ejemplo: "F-2026-001" },
      { id: "factura_id", label: "ID interno de la factura", ejemplo: "FCT-001" },
      { id: "proveedor", label: "Proveedor", ejemplo: "Suministros XYZ" },
      { id: "fecha_vencimiento", label: "Fecha de vencimiento", ejemplo: "10/03/2026" },
      { id: "dias_atraso", label: "Días de atraso", ejemplo: "5" },
      { id: "porcentaje", label: "Porcentaje", ejemplo: "15%" },
    ]
  },
  sistema: {
    label: "Sistema",
    campos: [
      { id: "logo_url", label: "URL del logo", ejemplo: "https://...logo.png" },
      { id: "base_url", label: "URL base del sistema", ejemplo: "https://softwareia.qasesores.com.do" },
      { id: "fecha", label: "Fecha actual", ejemplo: "15/02/2026" },
      { id: "hora", label: "Hora actual", ejemplo: "10:30" },
      { id: "sistema.nombre_empresa", label: "Nombre de la empresa", ejemplo: "QAsesores SRL" },
      { id: "sistema.url", label: "URL del sistema", ejemplo: "https://app.qasesores.com" },
      { id: "sistema.anio", label: "Año actual", ejemplo: "2026" },
    ]
  },
  examen: {
    label: "Exámenes y Pruebas",
    campos: [
      { id: "examen_titulo", label: "Título del examen", ejemplo: "Evaluación de Seguros Básicos" },
      { id: "nombre_candidato", label: "Nombre del candidato/evaluado", ejemplo: "Juan Pérez" },
      { id: "email_candidato", label: "Email del candidato", ejemplo: "juan@ejemplo.com" },
      { id: "tiempo_limite", label: "Tiempo límite (minutos)", ejemplo: "30" },
      { id: "enlace_examen", label: "Enlace al examen", ejemplo: "https://app.../examen/abc123" },
      { id: "calificacion", label: "Calificación obtenida (%)", ejemplo: "85" },
      { id: "resultado", label: "Resultado (Aprobado/No Aprobado)", ejemplo: "Aprobado" },
      { id: "color_calificacion", label: "Color según resultado", ejemplo: "#16a34a" },
      { id: "tiempo_usado", label: "Tiempo utilizado (minutos)", ejemplo: "22" },
      { id: "total_incidencias", label: "Total de incidencias", ejemplo: "0" },
      { id: "comentario_evaluador", label: "Comentario del evaluador", ejemplo: "Buen desempeño" },
      { id: "evaluado_por", label: "Nombre del evaluador", ejemplo: "María García" },
      { id: "enviado_por", label: "Enviado por", ejemplo: "Admin Sistema" },
      { id: "estado_examen", label: "Estado del examen", ejemplo: "Completado" },
      { id: "minutos_transcurridos", label: "Minutos transcurridos", ejemplo: "45" },
      { id: "nota_minima", label: "Nota mínima de aprobación", ejemplo: "70" },
    ]
  }
};

// Plantillas predefinidas de ejemplo
const PLANTILLAS_EJEMPLO = [
  {
    nombre: "Nuevo Ticket Asignado",
    asunto: "Se te ha asignado el ticket {{ticket.id}}",
    categoria: "mesa_ayuda",
    contenido: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-bottom: 3px solid #0C314C; background-color: #f8f9fa;">
    <tr><td align="center" style="padding: 15px 20px;"><img src="{{sistema.logo_url}}" alt="{{sistema.nombre_empresa}}" width="150" /></td></tr>
  </table>
  <table width="100%" cellpadding="0" cellspacing="0" border="0">
    <tr><td style="padding: 25px 20px;">
      <h2 style="color: #0C314C; margin: 0 0 15px 0;">Nuevo Ticket Asignado</h2>
      <p style="color: #333; font-size: 15px;">Hola <strong>{{user.name}}</strong>,</p>
      <p style="color: #333; font-size: 15px;">Se te ha asignado un nuevo ticket:</p>
      <table width="100%" style="background: #f8f9fa; border-left: 4px solid #0C314C; margin: 20px 0;"><tr><td style="padding: 15px;">
        <p style="margin: 5px 0;"><strong>ID:</strong> {{ticket.id}}</p>
        <p style="margin: 5px 0;"><strong>Título:</strong> {{ticket.titulo}}</p>
        <p style="margin: 5px 0;"><strong>Prioridad:</strong> {{ticket.prioridad}}</p>
      </td></tr></table>
      <table width="100%"><tr><td align="center" style="padding: 15px 0;">
        <table><tr><td bgcolor="#0C314C" style="border-radius: 6px;"><a href="{{sistema.url}}/mesa-ayuda" style="display: inline-block; padding: 12px 30px; color: #fff; text-decoration: none; font-weight: bold;">Ver Ticket</a></td></tr></table>
      </td></tr></table>
    </td></tr>
  </table>
  <table width="100%" style="background: #f8f9fa; border-top: 1px solid #eee;"><tr><td align="center" style="padding: 15px;"><p style="color: #666; font-size: 11px; margin: 0;">{{sistema.nombre_empresa}} - Mesa de Ayuda</p></td></tr></table>
</div>`
  },
  {
    nombre: "Solicitud de Permiso Aprobada",
    asunto: "Tu solicitud de permiso ha sido aprobada",
    categoria: "gestion_humana",
    contenido: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-bottom: 3px solid #28a745; background-color: #f8f9fa;">
    <tr><td align="center" style="padding: 15px 20px;"><img src="{{sistema.logo_url}}" alt="{{sistema.nombre_empresa}}" width="150" /></td></tr>
  </table>
  <table width="100%" cellpadding="0" cellspacing="0" border="0">
    <tr><td style="padding: 25px 20px;">
      <h2 style="color: #28a745; margin: 0 0 15px 0;">✓ Solicitud Aprobada</h2>
      <p style="color: #333; font-size: 15px;">Estimado/a <strong>{{user.name}}</strong>,</p>
      <p style="color: #333; font-size: 15px;">Su solicitud de <strong>{{solicitud.tipo}}</strong> ha sido <span style="color: #28a745; font-weight: bold;">APROBADA</span>.</p>
      <table width="100%" style="background: #d4edda; border-left: 4px solid #28a745; margin: 20px 0;"><tr><td style="padding: 15px;">
        <p style="margin: 5px 0; color: #155724;"><strong>Fecha inicio:</strong> {{solicitud.fecha_inicio}}</p>
        <p style="margin: 5px 0; color: #155724;"><strong>Fecha fin:</strong> {{solicitud.fecha_fin}}</p>
        <p style="margin: 5px 0; color: #155724;"><strong>Motivo:</strong> {{solicitud.motivo}}</p>
      </td></tr></table>
      <table width="100%"><tr><td align="center" style="padding: 15px 0;">
        <table><tr><td bgcolor="#28a745" style="border-radius: 6px;"><a href="{{sistema.url}}/gestion-humana/solicitudes" style="display: inline-block; padding: 12px 30px; color: #fff; text-decoration: none; font-weight: bold;">Ver Solicitud</a></td></tr></table>
      </td></tr></table>
    </td></tr>
  </table>
  <table width="100%" style="background: #f8f9fa; border-top: 1px solid #eee;"><tr><td align="center" style="padding: 15px;"><p style="color: #666; font-size: 11px; margin: 0;">{{sistema.nombre_empresa}} - Gestión Humana</p></td></tr></table>
</div>`
  },
  {
    nombre: "Factura Próxima a Vencer",
    asunto: "Alerta: Factura {{factura.numero}} próxima a vencer",
    categoria: "cuentas_pagar",
    contenido: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-bottom: 3px solid #ffc107; background-color: #f8f9fa;">
    <tr><td align="center" style="padding: 15px 20px;"><img src="{{sistema.logo_url}}" alt="{{sistema.nombre_empresa}}" width="150" /></td></tr>
  </table>
  <table width="100%" cellpadding="0" cellspacing="0" border="0">
    <tr><td style="padding: 25px 20px;">
      <h2 style="color: #856404; margin: 0 0 15px 0;">⚠️ Alerta de Vencimiento</h2>
      <p style="color: #333; font-size: 15px;">La siguiente factura está próxima a vencer:</p>
      <table width="100%" style="background: #fff3cd; border-left: 4px solid #ffc107; margin: 20px 0;"><tr><td style="padding: 15px;">
        <p style="margin: 5px 0; color: #856404;"><strong>Factura:</strong> {{factura.numero}}</p>
        <p style="margin: 5px 0; color: #856404;"><strong>Proveedor:</strong> {{factura.proveedor}}</p>
        <p style="margin: 5px 0; color: #856404;"><strong>Monto:</strong> {{factura.monto}}</p>
        <p style="margin: 5px 0; color: #dc3545;"><strong>Vencimiento:</strong> {{factura.fecha_vencimiento}}</p>
      </td></tr></table>
      <table width="100%"><tr><td align="center" style="padding: 15px 0;">
        <table><tr><td bgcolor="#ffc107" style="border-radius: 6px;"><a href="{{sistema.url}}/cuentas-pagar" style="display: inline-block; padding: 12px 30px; color: #212529; text-decoration: none; font-weight: bold;">Ver Factura</a></td></tr></table>
      </td></tr></table>
    </td></tr>
  </table>
  <table width="100%" style="background: #f8f9fa; border-top: 1px solid #eee;"><tr><td align="center" style="padding: 15px;"><p style="color: #666; font-size: 11px; margin: 0;">{{sistema.nombre_empresa}} - Cuentas por Pagar</p></td></tr></table>
</div>`
  },
  {
    nombre: "Requisición Pendiente de Aprobación",
    asunto: "Nueva requisición pendiente: {{requisicion.titulo}}",
    categoria: "requisiciones",
    contenido: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
  <table width="100%" cellpadding="0" cellspacing="0" border="0" style="border-bottom: 3px solid #17a2b8; background-color: #f8f9fa;">
    <tr><td align="center" style="padding: 15px 20px;"><img src="{{sistema.logo_url}}" alt="{{sistema.nombre_empresa}}" width="150" /></td></tr>
  </table>
  <table width="100%" cellpadding="0" cellspacing="0" border="0">
    <tr><td style="padding: 25px 20px;">
      <h2 style="color: #17a2b8; margin: 0 0 15px 0;">Nueva Requisición Pendiente</h2>
      <p style="color: #333; font-size: 15px;">Hay una nueva requisición que requiere tu aprobación:</p>
      <table width="100%" style="background: #d1ecf1; border-left: 4px solid #17a2b8; margin: 20px 0;"><tr><td style="padding: 15px;">
        <p style="margin: 5px 0; color: #0c5460;"><strong>ID:</strong> {{requisicion.id}}</p>
        <p style="margin: 5px 0; color: #0c5460;"><strong>Título:</strong> {{requisicion.titulo}}</p>
        <p style="margin: 5px 0; color: #0c5460;"><strong>Solicitante:</strong> {{requisicion.solicitante}}</p>
        <p style="margin: 5px 0; color: #0c5460;"><strong>Total:</strong> {{requisicion.total}}</p>
      </td></tr></table>
      <table width="100%"><tr><td align="center" style="padding: 15px 0;">
        <table><tr><td bgcolor="#17a2b8" style="border-radius: 6px;"><a href="{{sistema.url}}/requisiciones" style="display: inline-block; padding: 12px 30px; color: #fff; text-decoration: none; font-weight: bold;">Ver Requisición</a></td></tr></table>
      </td></tr></table>
    </td></tr>
  </table>
  <table width="100%" style="background: #f8f9fa; border-top: 1px solid #eee;"><tr><td align="center" style="padding: 15px;"><p style="color: #666; font-size: 11px; margin: 0;">{{sistema.nombre_empresa}} - Requisiciones</p></td></tr></table>
</div>`
  },
  {
    nombre: "Bienvenida a Nuevo Usuario",
    asunto: "Bienvenido/a a {{sistema.nombre_empresa}}",
    categoria: "sistema",
    contenido: `<div style="font-family: Arial, sans-serif; max-width: 600px; margin: 0 auto; background: #ffffff;">
  <!-- Logo Header -->
  <div style="text-align: center; padding: 15px 20px; border-bottom: 3px solid #0C314C; background: #f8f9fa;">
    <img src="{{sistema.logo_url}}" alt="{{sistema.nombre_empresa}}" style="max-height: 50px; max-width: 180px; width: auto; height: auto;" onerror="this.style.display='none'">
  </div>
  
  <div style="padding: 25px 20px;">
    <h2 style="color: #0C314C; margin-top: 0; font-size: 22px;">¡Bienvenido/a!</h2>
    <p style="color: #333; font-size: 15px; line-height: 1.6; margin-bottom: 15px;">Estimado/a <strong>{{user.name}}</strong>,</p>
    <p style="color: #333; font-size: 15px; line-height: 1.6; margin-bottom: 20px;">Tu cuenta ha sido creada exitosamente en el sistema de gestión de <strong>{{sistema.nombre_empresa}}</strong>.</p>
    
    <div style="background: #f8f9fa; padding: 15px; border-radius: 8px; margin: 20px 0; border-left: 4px solid #0C314C;">
      <table style="width: 100%; border-collapse: collapse;">
        <tr>
          <td style="padding: 6px 0; color: #666; font-size: 14px; width: 120px;"><strong>Email:</strong></td>
          <td style="padding: 6px 0; color: #333; font-size: 14px;">{{user.email}}</td>
        </tr>
        <tr>
          <td style="padding: 6px 0; color: #666; font-size: 14px;"><strong>Rol:</strong></td>
          <td style="padding: 6px 0; color: #333; font-size: 14px;">{{user.role}}</td>
        </tr>
        <tr>
          <td style="padding: 6px 0; color: #666; font-size: 14px;"><strong>Departamento:</strong></td>
          <td style="padding: 6px 0; color: #333; font-size: 14px;">{{user.departamento}}</td>
        </tr>
      </table>
    </div>
    
    <p style="color: #333; font-size: 15px; line-height: 1.6; margin-bottom: 20px;">Para comenzar a usar el sistema, haz clic en el siguiente botón:</p>
    
    <!-- Button centered -->
    <table cellspacing="0" cellpadding="0" style="margin: 20px auto;">
      <tr>
        <td align="center" style="border-radius: 6px; background-color: #0C314C;">
          <a href="{{sistema.url}}/login" target="_blank" style="display: inline-block; padding: 12px 28px; font-size: 15px; color: #ffffff; text-decoration: none; font-weight: bold; border-radius: 6px;">
            Iniciar Sesión
          </a>
        </td>
      </tr>
    </table>
    
    <p style="color: #333; font-size: 13px; line-height: 1.5; background: #fff3cd; padding: 10px 12px; border-radius: 6px; border-left: 4px solid #ffc107; margin-top: 20px;">
      <strong>⚠️ Importante:</strong> Por seguridad, deberás cambiar tu contraseña en el primer inicio de sesión.
    </p>
  </div>
  
  <!-- Footer -->
  <div style="background: #f8f9fa; padding: 15px 20px; text-align: center; border-top: 1px solid #eee;">
    <p style="color: #666; font-size: 11px; margin: 0;">Este es un mensaje automático del sistema de {{sistema.nombre_empresa}}.</p>
    <p style="color: #999; font-size: 10px; margin: 8px 0 0 0;">Si tienes dudas, contacta al departamento de TI.</p>
  </div>
</div>`
  }
];

const CATEGORIAS = [
  { id: "mesa_ayuda", label: "Mesa de Ayuda" },
  { id: "gestion_humana", label: "Gestión Humana" },
  { id: "requisiciones", label: "Requisiciones" },
  { id: "cuentas_pagar", label: "Cuentas por Pagar" },
  { id: "visitantes", label: "Control de Visitantes" },
  { id: "mensajeria", label: "Mensajería" },
  { id: "riesgos_generales", label: "Riesgos Generales" },
  { id: "riesgos_personas", label: "Riesgos de Personas" },
  { id: "sistema", label: "Sistema" },
  { id: "sistema_ponche", label: "Sistema de Ponche" },
  { id: "hallazgos_auditoria", label: "Hallazgos Auditoria" },
  { id: "personalizado", label: "Personalizado" },
];

export default function PlantillasCorreosPage() {
  // Helper para filtrar CAMPOS_SISTEMA por término de búsqueda.
  // Matchea por id de variable o por label (case-insensitive).
  // Si el término matchea el label del grupo, devuelve TODOS los campos del grupo.
  const filterCampos = (term) => {
    const t = (term || "").trim().toLowerCase();
    if (!t) return CAMPOS_SISTEMA;
    const out = {};
    for (const [key, grupo] of Object.entries(CAMPOS_SISTEMA)) {
      const grupoMatch = (grupo.label || "").toLowerCase().includes(t);
      const campos = grupo.campos.filter(c =>
        (c.id || "").toLowerCase().includes(t) ||
        (c.label || "").toLowerCase().includes(t)
      );
      if (grupoMatch || campos.length > 0) {
        out[key] = { ...grupo, campos: grupoMatch ? grupo.campos : campos };
      }
    }
    return out;
  };

  const [plantillas, setPlantillas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showCamposRef, setShowCamposRef] = useState(false);
  // Buscadores para los popups de Variables del Sistema (uno por popup)
  const [varSearchEditor, setVarSearchEditor] = useState("");
  const [varSearchRef, setVarSearchRef] = useState("");
  const [editingPlantilla, setEditingPlantilla] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterCategoria, setFilterCategoria] = useState("all");
  const [saving, setSaving] = useState(false);
  
  // Estados para envío de correos
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [sendingPlantilla, setSendingPlantilla] = useState(null);
  const [usuarios, setUsuarios] = useState([]);
  const [configUserSearch, setConfigUserSearch] = useState("");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);
  const [userSearchTerm, setUserSearchTerm] = useState("");
  const [sendResult, setSendResult] = useState(null);
  
  // Estados para vista previa completa
  const [previewVars, setPreviewVars] = useState({});
  const [previewHtml, setPreviewHtml] = useState("");
  const [previewAsunto, setPreviewAsunto] = useState("");
  const [previewDetectedVars, setPreviewDetectedVars] = useState([]);
  const [loadingPreview, setLoadingPreview] = useState(false);
  
  // Estados para configuración del logo
  const [showConfigDialog, setShowConfigDialog] = useState(false);
  const [logoConfig, setLogoConfig] = useState({
    logo_url: "",
    footer_text: "",
    primary_color: "#0C314C"
  });
  const [savingConfig, setSavingConfig] = useState(false);
  const [isDraggingLogo, setIsDraggingLogo] = useState(false);
  
  // Estados para sincronización de plantillas
  const [syncStatus, setSyncStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [updatingUrls, setUpdatingUrls] = useState(false);

  // Estado para el botón de respaldo de Vacaciones Aprobadas → Gestión Humana
  const [backfillStatus, setBackfillStatus] = useState(null);
  const [showBackfillDialog, setShowBackfillDialog] = useState(false);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);
  
  // Módulos y eventos para configuración
  const [modulosEventos, setModulosEventos] = useState({ modulos: {}, destinatarios: [] });
  const [expandedCategories, setExpandedCategories] = useState({});
  const [viewMode, setViewMode] = useState("grouped"); // "grouped" o "grid"
  
  // Formulario
  const [formData, setFormData] = useState({
    nombre: "",
    asunto: "",
    nombre_remitente: "",
    categoria: "personalizado",
    contenido: "",
    activa: true,
    configuracion: {
      modulo: "",
      evento: "",
      destinatarios: [],
      emails_adicionales: [],
      condiciones: null,
      envio_automatico: false
    }
  });
  const [editorTab, setEditorTab] = useState("editor"); // "editor", "preview", "config", "auditoria"
  const [emailInput, setEmailInput] = useState(""); // Para agregar emails adicionales
  const [auditoriaData, setAuditoriaData] = useState(null);
  const [auditoriaLoading, setAuditoriaLoading] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [triggeringManual, setTriggeringManual] = useState(false);
  
  const { getAuthHeaders, user } = useAuth();
  const isAdmin = ["admin", "administrador"].includes(user?.role?.toLowerCase());

  const fetchPlantillas = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/plantillas-correo`, getAuthHeaders());
      setPlantillas(res.data);
    } catch (err) {
      console.error("Error loading templates:", err);
      toast.error("Error al cargar plantillas");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  // Cargar configuración del logo
  const fetchLogoConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/plantillas-correo/config/general`, getAuthHeaders());
      setLogoConfig(res.data);
    } catch (err) {
      console.error("Error loading logo config:", err);
    }
  }, [getAuthHeaders]);

  // Verificar estado de sincronización de plantillas
  const checkSyncStatus = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/plantillas-correo/sincronizar/estado`, getAuthHeaders());
      setSyncStatus(res.data);
    } catch (err) {
      console.error("Error checking sync status:", err);
    }
  }, [getAuthHeaders]);

  // Estado del botón de respaldo de Vacaciones Aprobadas → Gestión Humana
  const loadBackfillStatus = useCallback(async () => {
    if (!isAdmin) return;
    try {
      const res = await axios.get(`${API}/plantillas-correo/backfill-vacaciones-gh/status`, getAuthHeaders());
      setBackfillStatus(res.data);
    } catch (err) {
      console.error("Error loading backfill status:", err);
    }
  }, [getAuthHeaders, isAdmin]);

  const runBackfillVacacionesGH = async () => {
    setBackfillRunning(true);
    try {
      const res = await axios.post(`${API}/plantillas-correo/backfill-vacaciones-gh`, {}, getAuthHeaders());
      setBackfillResult(res.data);
      toast.success(`Respaldo enviado: ${res.data.enviadas} correo(s) a Gestión Humana`);
      loadBackfillStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "No se pudo ejecutar el respaldo");
    } finally {
      setBackfillRunning(false);
    }
  };

  // Sincronizar plantillas faltantes
  const handleSyncPlantillas = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/plantillas-correo/inicializar`, {}, getAuthHeaders());
      toast.success(`${res.data.created} plantillas creadas. Total: ${res.data.total_en_sistema}`);
      fetchPlantillas();
      checkSyncStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al sincronizar plantillas");
    } finally {
      setSyncing(false);
    }
  };

  // Actualizar URLs de todas las plantillas (solo agrega nuevas, no sobrescribe)
  const handleUpdateUrls = async () => {
    setUpdatingUrls(true);
    try {
      const res = await axios.post(`${API}/plantillas-correo/actualizar-urls`, {}, getAuthHeaders());
      if (res.data.created > 0) {
        toast.success(`${res.data.created} plantillas nuevas agregadas. ${res.data.skipped} existentes respetadas.`);
      } else {
        toast.info(`No hay plantillas nuevas. ${res.data.skipped} plantillas existentes respetadas.`);
      }
      fetchPlantillas();
      checkSyncStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al sincronizar plantillas");
    } finally {
      setUpdatingUrls(false);
    }
  };

  // Forzar actualización de todas las plantillas (solo admin)
  const handleForceUpdate = async () => {
    if (!confirm("⚠️ ATENCIÓN: Esto sobrescribirá TODAS las plantillas existentes con la versión del sistema. ¿Está seguro de continuar?")) {
      return;
    }
    setUpdatingUrls(true);
    try {
      const res = await axios.post(`${API}/plantillas-correo/forzar-actualizacion`, {}, getAuthHeaders());
      toast.success(`${res.data.updated} plantillas actualizadas, ${res.data.created} creadas`);
      fetchPlantillas();
      checkSyncStatus();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al forzar actualización");
    } finally {
      setUpdatingUrls(false);
    }
  };

  // Cargar módulos y eventos disponibles
  const fetchModulosEventos = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/plantillas-correo/config/modulos-eventos`, getAuthHeaders());
      setModulosEventos(res.data);
    } catch (err) {
      console.error("Error loading modulos/eventos:", err);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchPlantillas();
    fetchLogoConfig();
    checkSyncStatus();
    fetchModulosEventos();
    loadBackfillStatus();
  }, [fetchPlantillas, fetchLogoConfig, checkSyncStatus, fetchModulosEventos, loadBackfillStatus]);

  const handleSaveLogoConfig = async () => {
    try {
      setSavingConfig(true);
      await axios.put(`${API}/plantillas-correo/config/general`, logoConfig, getAuthHeaders());
      toast.success("Configuración guardada");
      setShowConfigDialog(false);
    } catch (err) {
      toast.error("Error al guardar configuración");
    } finally {
      setSavingConfig(false);
    }
  };

  // Función para procesar archivo de logo (compartida entre click y drag-drop)
  const processLogoFile = async (file) => {
    if (!file) return;

    // Validar tipo
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp", "image/svg+xml"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Solo se permiten archivos de imagen (JPEG, PNG, GIF, WebP, SVG)");
      return;
    }

    // Validar tamaño (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast.error("El archivo no debe superar 2MB");
      return;
    }

    try {
      setSavingConfig(true);
      const formData = new FormData();
      formData.append("file", file);

      const res = await axios.post(
        `${API}/plantillas-correo/config/upload-logo`,
        formData,
        {
          ...getAuthHeaders(),
          headers: {
            ...getAuthHeaders().headers,
            "Content-Type": "multipart/form-data"
          }
        }
      );

      setLogoConfig(prev => ({ ...prev, logo_url: res.data.logo_url }));
      toast.success("Logo subido y almacenado correctamente en la nube");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al subir el logo");
    } finally {
      setSavingConfig(false);
      setIsDraggingLogo(false);
    }
  };

  // Handler para input file
  const handleLogoUpload = async (e) => {
    const file = e.target.files[0];
    await processLogoFile(file);
  };

  // Drag and drop handlers para logo
  const handleLogoDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(true);
  };

  const handleLogoDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDraggingLogo(false);
  };

  const handleLogoDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleLogoDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDraggingLogo(false);

    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
      await processLogoFile(files[0]);
    }
  };

  // Cargar usuarios cuando se abre el diálogo de envío
  const fetchUsuarios = useCallback(async () => {
    try {
      setLoadingUsers(true);
      const res = await axios.get(`${API}/users`, getAuthHeaders());
      setUsuarios(res.data.filter(u => u.email)); // Solo usuarios con email
    } catch (err) {
      console.error("Error loading users:", err);
      toast.error("Error al cargar usuarios");
    } finally {
      setLoadingUsers(false);
    }
  }, [getAuthHeaders]);

  // Cargar usuarios al montar para el selector de usuarios específicos en la config
  useEffect(() => {
    fetchUsuarios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenSendDialog = async (plantilla) => {
    setSendingPlantilla(plantilla);
    setSelectedUsers([]);
    setUserSearchTerm("");
    setSendResult(null);
    setShowSendDialog(true);
    await fetchUsuarios();
  };

  const handleToggleUser = (userId) => {
    setSelectedUsers(prev => 
      prev.includes(userId) 
        ? prev.filter(id => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAll = () => {
    const filteredIds = filteredUsuarios.map(u => u.id);
    if (selectedUsers.length === filteredIds.length) {
      setSelectedUsers([]);
    } else {
      setSelectedUsers(filteredIds);
    }
  };

  const handleSendEmails = async () => {
    if (selectedUsers.length === 0) {
      toast.error("Seleccione al menos un usuario");
      return;
    }

    try {
      setSendingEmails(true);
      const res = await axios.post(
        `${API}/plantillas-correo/${sendingPlantilla.id}/enviar`,
        { plantilla_id: sendingPlantilla.id, user_ids: selectedUsers },
        getAuthHeaders()
      );
      
      setSendResult(res.data);
      
      if (res.data.enviados > 0) {
        toast.success(`Correo enviado a ${res.data.enviados} usuario(s)`);
      }
      if (res.data.errores > 0) {
        toast.warning(`${res.data.errores} envío(s) fallido(s)`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al enviar correos");
    } finally {
      setSendingEmails(false);
    }
  };

  const filteredUsuarios = usuarios.filter(u => {
    const term = userSearchTerm.toLowerCase();
    return (
      u.name?.toLowerCase().includes(term) ||
      u.email?.toLowerCase().includes(term) ||
      u.departamento_nombre?.toLowerCase().includes(term)
    );
  });

  const defaultConfiguracion = {
    modulo: "",
    evento: "",
    destinatarios: [],
    emails_adicionales: [],
    condiciones: null,
    envio_automatico: false
  };

  // Helper para obtener configuración actual con valores por defecto
  const getConfig = () => formData.configuracion || defaultConfiguracion;
  
  // Helper para actualizar configuración
  const updateConfig = (updates) => {
    setFormData({
      ...formData,
      configuracion: { ...getConfig(), ...updates }
    });
  };

  const handleCreate = () => {
    setEditingPlantilla(null);
    setFormData({
      nombre: "",
      asunto: "",
      nombre_remitente: "",
      categoria: "personalizado",
      contenido: "",
      activa: true,
      configuracion: { ...defaultConfiguracion }
    });
    setEditorTab("editor");
    setEmailInput("");
    setShowEditor(true);
  };

  const handleEdit = (plantilla) => {
    setEditingPlantilla(plantilla);
    setFormData({
      nombre: plantilla.nombre,
      asunto: plantilla.asunto,
      nombre_remitente: plantilla.nombre_remitente || "",
      categoria: plantilla.categoria || "personalizado",
      contenido: plantilla.contenido,
      activa: plantilla.activa !== false,
      configuracion: plantilla.configuracion || { ...defaultConfiguracion },
      _visualConfig: plantilla.visual_config || null
    });
    // Auto-select visual tab if this template has visual config
    const isVacacionesInicio = plantilla.configuracion?.evento === "vacaciones_inicio";
    setEditorTab(isVacacionesInicio && plantilla.visual_config ? "visual" : "editor");
    setEmailInput("");
    setShowEditor(true);
  };

  const handleDuplicate = (plantilla) => {
    setEditingPlantilla(null);
    setFormData({
      nombre: `${plantilla.nombre} (Copia)`,
      asunto: plantilla.asunto,
      nombre_remitente: plantilla.nombre_remitente || "",
      categoria: plantilla.categoria || "personalizado",
      contenido: plantilla.contenido,
      activa: true,
      configuracion: plantilla.configuracion || { ...defaultConfiguracion }
    });
    setEditorTab("editor");
    setEmailInput("");
    setShowEditor(true);
  };

  const handleDelete = async (plantilla) => {
    if (!window.confirm(`¿Está seguro de eliminar la plantilla "${plantilla.nombre}"?`)) return;
    
    try {
      await axios.delete(`${API}/plantillas-correo/${plantilla.id}`, getAuthHeaders());
      toast.success("Plantilla eliminada");
      fetchPlantillas();
    } catch (err) {
      toast.error("Error al eliminar plantilla");
    }
  };

  const fetchAuditoriaVacaciones = useCallback(async (plantillaId) => {
    if (!plantillaId) return;
    setAuditoriaLoading(true);
    try {
      const [auditRes, statusRes] = await Promise.all([
        axios.get(`${API}/plantillas-correo/${plantillaId}/auditoria-vacaciones?dias_pasados=90&dias_futuros=60`, getAuthHeaders()),
        axios.get(`${API}/vacaciones-inicio/scheduler/status`, getAuthHeaders()).catch(() => ({ data: null })),
      ]);
      setAuditoriaData(auditRes.data);
      setSchedulerStatus(statusRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error cargando auditoría");
      setAuditoriaData(null);
    } finally {
      setAuditoriaLoading(false);
    }
  }, [getAuthHeaders]);

  const handleDispararManual = useCallback(async () => {
    if (!window.confirm("¿Disparar el scheduler AHORA, ignorando la ventana horaria?\n\nEsto procesará todas las vacaciones aprobadas que inicien HOY y cumplan el mínimo. Idempotente — no enviará duplicados.")) return;
    setTriggeringManual(true);
    try {
      const r = await axios.post(`${API}/vacaciones-inicio/scheduler/disparar-ahora`, {}, getAuthHeaders());
      if (r.data?.ok) {
        toast.success(r.data.mensaje || "Scheduler disparado");
        // Refrescar el status para ver los nuevos envíos
        if (editingPlantilla?.id) {
          setTimeout(() => fetchAuditoriaVacaciones(editingPlantilla.id), 2000);
        }
      } else {
        toast.error(r.data?.error || "Error al disparar");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setTriggeringManual(false);
    }
  }, [getAuthHeaders, editingPlantilla, fetchAuditoriaVacaciones]);


  const handleSave = async () => {
    if (!formData.nombre.trim()) {
      toast.error("El nombre es requerido");
      return;
    }
    if (!formData.asunto.trim()) {
      toast.error("El asunto es requerido");
      return;
    }
    if (!formData.contenido.trim()) {
      toast.error("El contenido es requerido");
      return;
    }

    try {
      setSaving(true);
      if (editingPlantilla) {
        const saveData = { ...formData };
        if (saveData._visualConfig) {
          saveData.visual_config = saveData._visualConfig;
        }
        delete saveData._visualConfig;
        await axios.put(
          `${API}/plantillas-correo/${editingPlantilla.id}`,
          saveData,
          getAuthHeaders()
        );
        toast.success("Plantilla actualizada");
      } else {
        const saveData = { ...formData };
        if (saveData._visualConfig) {
          saveData.visual_config = saveData._visualConfig;
        }
        delete saveData._visualConfig;
        await axios.post(`${API}/plantillas-correo`, saveData, getAuthHeaders());
        toast.success("Plantilla creada");
      }
      setShowEditor(false);
      fetchPlantillas();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar plantilla");
    } finally {
      setSaving(false);
    }
  };

  const handleCreateDefaults = async () => {
    if (!window.confirm("¿Crear las 5 plantillas de ejemplo predefinidas?")) return;
    
    try {
      setSaving(true);
      for (const plantilla of PLANTILLAS_EJEMPLO) {
        await axios.post(`${API}/plantillas-correo`, {
          ...plantilla,
          activa: true
        }, getAuthHeaders());
      }
      toast.success("Plantillas de ejemplo creadas");
      fetchPlantillas();
    } catch (err) {
      toast.error("Error al crear plantillas de ejemplo");
    } finally {
      setSaving(false);
    }
  };

  const insertVariable = (variable) => {
    const textarea = document.getElementById("contenido-editor");
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const text = formData.contenido;
      const newText = text.substring(0, start) + `{{${variable}}}` + text.substring(end);
      setFormData({ ...formData, contenido: newText });
      
      // Restaurar cursor
      setTimeout(() => {
        textarea.focus();
        textarea.selectionStart = start + variable.length + 4;
        textarea.selectionEnd = start + variable.length + 4;
      }, 0);
    } else {
      setFormData({ ...formData, contenido: formData.contenido + `{{${variable}}}` });
    }
  };

  const copyVariable = (variable) => {
    navigator.clipboard.writeText(`{{${variable}}}`);
    toast.success("Variable copiada al portapapeles");
  };


  // Helper: Get example value for a variable from CAMPOS_SISTEMA
  const getExampleForVar = (varName) => {
    for (const grupo of Object.values(CAMPOS_SISTEMA)) {
      for (const campo of grupo.campos) {
        if (campo.id === varName) return campo.ejemplo;
      }
    }
    // Common fallbacks
    const fallbacks = {
      "nombre_usuario": "Juan Pérez", "departamento": "Tecnología", "cargo": "Gerente",
      "fecha_inicio": "20/03/2026", "fecha_fin": "25/03/2026", "fecha_reintegro": "26/03/2026",
      "fecha_inicio_vacaciones": "20/03/2026", "fecha_fin_vacaciones": "25/03/2026",
      "dias_solicitados": "5", "dias_restantes": "9", "motivo": "Vacaciones programadas",
      "estado": "Aprobado", "tipo": "Vacaciones", "nombre_empleado": "Juan Pérez",
      "nombre_supervisor": "María García", "email_empleado": "juan@empresa.com",
    };
    return fallbacks[varName] || varName;
  };

  const handleOpenPreview = async (plantilla) => {
    setEditingPlantilla(plantilla);
    setPreviewHtml("");
    setPreviewAsunto("");
    setLoadingPreview(true);
    setShowPreview(true);
    
    try {
      // First call with no custom vars to get detected variables
      const res = await axios.post(`${API}/plantillas-correo/preview/${plantilla.id}`, { variables: {} }, getAuthHeaders());
      const detectedVars = res.data.variables_detectadas || [];
      setPreviewDetectedVars(detectedVars);
      
      // Pre-fill with example values
      const defaultVars = {};
      detectedVars.forEach(v => {
        defaultVars[v] = getExampleForVar(v);
      });
      setPreviewVars(defaultVars);
      
      // Re-call with example values to get full rendered preview
      const res2 = await axios.post(`${API}/plantillas-correo/preview/${plantilla.id}`, { variables: defaultVars }, getAuthHeaders());
      setPreviewHtml(res2.data.contenido);
      setPreviewAsunto(res2.data.asunto);
    } catch (err) {
      console.error("Error generating preview:", err);
      toast.error("Error al generar vista previa");
    } finally {
      setLoadingPreview(false);
    }
  };

  const handleRefreshPreview = async () => {
    if (!editingPlantilla) return;
    setLoadingPreview(true);
    try {
      const res = await axios.post(`${API}/plantillas-correo/preview/${editingPlantilla.id}`, { variables: previewVars }, getAuthHeaders());
      setPreviewHtml(res.data.contenido);
      setPreviewAsunto(res.data.asunto);
      toast.success("Vista previa actualizada");
    } catch (err) {
      console.error("Error refreshing preview:", err);
      toast.error("Error al actualizar vista previa");
    } finally {
      setLoadingPreview(false);
    }
  };

  const filteredPlantillas = plantillas.filter(p => {
    const matchesSearch = (p.nombre || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (p.asunto || "").toLowerCase().includes(searchTerm.toLowerCase());
    const catSel = CATEGORIAS.find(c => c.id === filterCategoria);
    const matchesCategoria = filterCategoria === "all" || p.categoria === filterCategoria || (catSel && p.categoria === catSel.label);
    return matchesSearch && matchesCategoria;
  });

  // Agrupar plantillas por categoría
  const groupedPlantillas = useMemo(() => {
    const groups = {};
    filteredPlantillas.forEach(p => {
      const cat = p.categoria || "personalizado";
      if (!groups[cat]) {
        groups[cat] = [];
      }
      groups[cat].push(p);
    });
    // Ordenar por categoría
    const orderedGroups = {};
    const categoryOrder = ["Mesa de Ayuda", "Requisiciones", "Gestión Humana", "Inventario", "Control de Visitantes", "Cuentas por Pagar", "WhatsApp", "Mensajería", "Riesgos Generales", "Riesgos de Personas", "Sistema", "personalizado"];
    categoryOrder.forEach(cat => {
      if (groups[cat]) {
        orderedGroups[cat] = groups[cat];
      }
    });
    // Agregar categorías que no están en el orden predefinido
    Object.keys(groups).forEach(cat => {
      if (!orderedGroups[cat]) {
        orderedGroups[cat] = groups[cat];
      }
    });
    return orderedGroups;
  }, [filteredPlantillas]);
  
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };
  
  const expandAllCategories = () => {
    const all = {};
    Object.keys(groupedPlantillas).forEach(cat => {
      all[cat] = true;
    });
    setExpandedCategories(all);
  };
  
  const collapseAllCategories = () => {
    setExpandedCategories({});
  };

  const getCategoriaLabel = (catId) => {
    const cat = CATEGORIAS.find(c => c.id === catId);
    return cat?.label || catId;
  };

  const getCategoriaColor = (catId) => {
    const colors = {
      mesa_ayuda: "bg-blue-100 text-blue-800",
      gestion_humana: "bg-green-100 text-green-800",
      requisiciones: "bg-purple-100 text-purple-800",
      cuentas_pagar: "bg-amber-100 text-amber-800",
      visitantes: "bg-cyan-100 text-cyan-800",
      mensajeria: "bg-orange-100 text-orange-800",
      riesgos_generales: "bg-red-100 text-red-800",
      riesgos_personas: "bg-indigo-100 text-indigo-800",
      sistema: "bg-slate-100 text-slate-800",
      sistema_ponche: "bg-teal-100 text-teal-800",
      personalizado: "bg-pink-100 text-pink-800"
    };
    return colors[catId] || "bg-gray-100 text-gray-800";
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-slate-50 dark:bg-slate-900">
        <Sidebar />
        <main className="ml-0 md:ml-64 flex-1 flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-slate-50 dark:bg-slate-900">
      <Sidebar />
      <main className="ml-0 md:ml-64 flex-1 p-6 overflow-auto" data-testid="plantillas-correos-page">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex justify-between items-start mb-6">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 dark:text-white flex items-center gap-2">
                <Mail className="h-7 w-7 text-blue-600" />
                Plantillas de Correos
              </h1>
              <p className="text-slate-500 dark:text-slate-400">
                Administre las plantillas de correo electrónico del sistema
              </p>
            </div>
            <div className="flex gap-2 flex-wrap">
              {/* Botón para sincronizar plantillas nuevas - No sobrescribe existentes */}
              <Button 
                variant="outline" 
                onClick={handleUpdateUrls}
                disabled={updatingUrls}
                className="border-blue-300 text-blue-600 hover:bg-blue-50"
                title="Agrega plantillas nuevas sin sobrescribir las existentes"
              >
                {updatingUrls ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Sincronizar Nuevas
              </Button>
              {/* Botón de forzar actualización - Solo admin, sobrescribe todo */}
              {isAdmin && (
                <Button 
                  variant="outline" 
                  onClick={handleForceUpdate}
                  disabled={updatingUrls}
                  className="border-red-300 text-red-600 hover:bg-red-50"
                  title="⚠️ Sobrescribe TODAS las plantillas con la versión del sistema"
                >
                  {updatingUrls ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <AlertTriangle className="h-4 w-4 mr-2" />
                  )}
                  Restaurar Todo
                </Button>
              )}
              {/* Botón de sincronización - visible cuando hay plantillas faltantes */}
              {isAdmin && syncStatus && syncStatus.faltantes > 0 && (
                <Button 
                  variant="outline" 
                  onClick={handleSyncPlantillas}
                  disabled={syncing}
                  className="border-orange-300 text-orange-600 hover:bg-orange-50"
                >
                  {syncing ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Sincronizar ({syncStatus.faltantes} faltantes)
                </Button>
              )}
              {/* Badge de sincronizado */}
              {syncStatus && syncStatus.faltantes === 0 && plantillas.length > 0 && (
                <Badge variant="outline" className="bg-green-50 text-green-600 border-green-200 py-2 px-3">
                  <CheckCircle className="h-4 w-4 mr-1" />
                  Sincronizado
                </Badge>
              )}
              {isAdmin && (
                <Button variant="outline" onClick={() => setShowConfigDialog(true)}>
                  <Settings className="h-4 w-4 mr-2" />
                  Configurar Logo
                </Button>
              )}
              {isAdmin && (
                <Button
                  variant="outline"
                  onClick={() => { setBackfillResult(null); setShowBackfillDialog(true); loadBackfillStatus(); }}
                  disabled={backfillStatus?.executed}
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-60"
                  title="Reenvía a Gestión Humana las vacaciones aprobadas desde el 01 de abril (respaldo para expedientes)"
                  data-testid="backfill-vacaciones-gh-btn"
                >
                  {backfillStatus?.executed ? <CheckCircle className="h-4 w-4 mr-2" /> : <Send className="h-4 w-4 mr-2" />}
                  {backfillStatus?.executed ? "Respaldo Vac. enviado" : "Respaldo Vacaciones → GH"}
                </Button>
              )}
              <Button variant="outline" onClick={() => setShowCamposRef(true)}>
                <Variable className="h-4 w-4 mr-2" />
                Variables del Sistema
              </Button>
              {isAdmin && plantillas.length === 0 && (
                <Button variant="outline" onClick={handleCreateDefaults} disabled={saving}>
                  <RefreshCw className={`h-4 w-4 mr-2 ${saving ? 'animate-spin' : ''}`} />
                  Crear Ejemplos
                </Button>
              )}
              {isAdmin && (
                <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleCreate}>
                  <Plus className="h-4 w-4 mr-2" />
                  Nueva Plantilla
                </Button>
              )}
            </div>
          </div>

          {/* Diálogo de respaldo: Vacaciones Aprobadas → Gestión Humana */}
          <Dialog open={showBackfillDialog} onOpenChange={setShowBackfillDialog}>
            <DialogContent className="max-w-lg dark:bg-slate-800" data-testid="backfill-dialog">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Send className="h-5 w-5 text-emerald-600" />
                  Respaldo de Vacaciones Aprobadas → Gestión Humana
                </DialogTitle>
                <DialogDescription>
                  Reenvía a <strong>Gestión Humana</strong> la plantilla "Vacaciones Aprobadas" por cada
                  vacación aprobada entre el <strong>{backfillStatus?.rango?.desde || "01-04-2026"}</strong> y
                  <strong> hoy</strong>, como aval para los expedientes de los empleados.
                </DialogDescription>
              </DialogHeader>

              {!backfillResult ? (
                <div className="space-y-3 py-2">
                  <div className="rounded-lg border bg-slate-50 dark:bg-slate-900 p-3 text-sm space-y-1">
                    <div className="flex justify-between"><span className="text-slate-500">Vacaciones en el rango:</span><strong data-testid="backfill-total">{backfillStatus?.total_en_rango ?? "—"}</strong></div>
                    <div className="flex justify-between"><span className="text-slate-500">Pendientes por enviar:</span><strong className="text-emerald-700" data-testid="backfill-pendientes">{backfillStatus?.pendientes ?? "—"}</strong></div>
                    <div className="flex justify-between"><span className="text-slate-500">Destinatarios (GH):</span><strong>{backfillStatus?.gestion_humana_emails?.length || 0}</strong></div>
                  </div>
                  {backfillStatus?.executed && (
                    <p className="text-amber-600 text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> Este respaldo ya fue ejecutado anteriormente. Las vacaciones ya enviadas se omitirán automáticamente.</p>
                  )}
                  {(!backfillStatus?.gestion_humana_emails || backfillStatus.gestion_humana_emails.length === 0) && (
                    <p className="text-red-600 text-sm flex items-center gap-2"><AlertTriangle className="h-4 w-4" /> No hay usuarios de Gestión Humana con correo. No se puede enviar.</p>
                  )}
                  <p className="text-xs text-slate-500">Solo se envía a Gestión Humana (no al empleado). Es un botón de un solo uso.</p>
                </div>
              ) : (
                <div className="space-y-2 py-2 text-sm" data-testid="backfill-result">
                  <div className="rounded-lg border bg-emerald-50 dark:bg-emerald-900/20 p-3 space-y-1">
                    <div className="flex justify-between"><span>Enviadas a GH:</span><strong className="text-emerald-700">{backfillResult.enviadas}</strong></div>
                    <div className="flex justify-between"><span>Omitidas (ya enviadas):</span><strong>{backfillResult.omitidas_ya_enviadas}</strong></div>
                    <div className="flex justify-between"><span>Con error:</span><strong className={backfillResult.errores ? "text-red-600" : ""}>{backfillResult.errores}</strong></div>
                  </div>
                  {backfillResult.detalle_errores?.length > 0 && (
                    <div className="max-h-32 overflow-auto text-xs text-red-600 border rounded p-2">
                      {backfillResult.detalle_errores.map((e, i) => (
                        <div key={i}>• {e.empleado || e.solicitud_id}: {e.motivo}</div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <DialogFooter>
                {!backfillResult ? (
                  <>
                    <Button variant="outline" onClick={() => setShowBackfillDialog(false)} data-testid="backfill-cancel-btn">Cancelar</Button>
                    <Button
                      className="bg-emerald-600 hover:bg-emerald-700"
                      onClick={runBackfillVacacionesGH}
                      disabled={backfillRunning || !backfillStatus?.gestion_humana_emails?.length || (backfillStatus?.pendientes ?? 0) === 0}
                      data-testid="backfill-confirm-btn"
                    >
                      {backfillRunning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
                      {(backfillStatus?.pendientes ?? 0) === 0 ? "Nada por enviar" : `Enviar ${backfillStatus?.pendientes ?? ""} a Gestión Humana`}
                    </Button>
                  </>
                ) : (
                  <Button variant="outline" onClick={() => setShowBackfillDialog(false)} data-testid="backfill-close-btn">Cerrar</Button>
                )}
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Filtros */}
          <Card className="mb-6 dark:bg-slate-800 dark:border-slate-700">
            <CardContent className="p-4">
              <div className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre o asunto..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterCategoria} onValueChange={setFilterCategoria}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue placeholder="Categoría" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las categorías</SelectItem>
                    {CATEGORIAS.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Lista de plantillas */}
          {filteredPlantillas.length === 0 ? (
            <Card className="dark:bg-slate-800 dark:border-slate-700">
              <CardContent className="p-8 text-center">
                <Mail className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <h3 className="text-lg font-medium text-slate-700 dark:text-slate-300 mb-2">
                  {searchTerm || filterCategoria !== "all" ? "No se encontraron plantillas" : "No hay plantillas creadas"}
                </h3>
                <p className="text-slate-500 dark:text-slate-400 mb-4">
                  {searchTerm || filterCategoria !== "all" 
                    ? "Intente con otros filtros de búsqueda"
                    : "Cree su primera plantilla de correo o use las plantillas de ejemplo"}
                </p>
                {isAdmin && !searchTerm && filterCategoria === "all" && (
                  <div className="flex gap-2 justify-center">
                    <Button variant="outline" onClick={handleCreateDefaults}>
                      Crear Plantillas de Ejemplo
                    </Button>
                    <Button className="bg-blue-600 hover:bg-blue-700" onClick={handleCreate}>
                      <Plus className="h-4 w-4 mr-2" />
                      Crear Plantilla
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {/* Controles de vista */}
              <div className="flex justify-between items-center">
                <div className="flex gap-2">
                  <Button 
                    variant={viewMode === "grouped" ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setViewMode("grouped")}
                  >
                    <List className="h-4 w-4 mr-1" />
                    Agrupado
                  </Button>
                  <Button 
                    variant={viewMode === "grid" ? "default" : "outline"} 
                    size="sm"
                    onClick={() => setViewMode("grid")}
                  >
                    <LayoutGrid className="h-4 w-4 mr-1" />
                    Cuadrícula
                  </Button>
                </div>
                {viewMode === "grouped" && (
                  <div className="flex gap-2">
                    <Button variant="outline" size="sm" onClick={expandAllCategories}>
                      Expandir Todo
                    </Button>
                    <Button variant="outline" size="sm" onClick={collapseAllCategories}>
                      Colapsar Todo
                    </Button>
                  </div>
                )}
              </div>

              {viewMode === "grouped" ? (
                /* Vista Agrupada por Categoría */
                <div className="space-y-4">
                  {Object.entries(groupedPlantillas).map(([categoria, plantillasGrupo]) => (
                    <Card key={categoria} className="overflow-hidden">
                      <div 
                        className={`flex items-center justify-between p-4 cursor-pointer hover:bg-slate-50 transition-colors ${getCategoriaColor(categoria).replace('text-', 'bg-').split(' ')[0]}/10`}
                        onClick={() => toggleCategory(categoria)}
                      >
                        <div className="flex items-center gap-3">
                          <Badge className={getCategoriaColor(categoria)}>
                            {getCategoriaLabel(categoria)}
                          </Badge>
                          <span className="font-medium text-slate-700">
                            {plantillasGrupo.length} plantilla{plantillasGrupo.length !== 1 ? 's' : ''}
                          </span>
                          <span className="text-sm text-slate-500">
                            ({plantillasGrupo.filter(p => p.activa !== false).length} activas, 
                            {plantillasGrupo.filter(p => p.configuracion?.envio_automatico).length} automáticas)
                          </span>
                        </div>
                        {expandedCategories[categoria] ? (
                          <ChevronUp className="h-5 w-5 text-slate-400" />
                        ) : (
                          <ChevronDown className="h-5 w-5 text-slate-400" />
                        )}
                      </div>
                      
                      {expandedCategories[categoria] && (
                        <CardContent className="pt-0 pb-4">
                          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
                            {plantillasGrupo.map(plantilla => (
                              <div 
                                key={plantilla.id}
                                className={`p-3 rounded-lg border bg-white hover:shadow-md transition-all ${
                                  plantilla.activa === false ? 'opacity-60 bg-slate-50' : ''
                                }`}
                              >
                                <div className="flex justify-between items-start mb-2">
                                  <h4 className="font-medium text-sm text-slate-800 flex-1 pr-2">{plantilla.nombre}</h4>
                                  <div className="flex gap-1 flex-shrink-0">
                                    {plantilla.activa !== false ? (
                                      <Badge className="bg-green-100 text-green-700 text-xs px-1.5 py-0.5">
                                        <CheckCircle className="h-2.5 w-2.5 mr-0.5" />
                                        Activa
                                      </Badge>
                                    ) : (
                                      <Badge variant="secondary" className="text-xs px-1.5 py-0.5">Inactiva</Badge>
                                    )}
                                    {plantilla.configuracion?.envio_automatico && (
                                      <Badge className="bg-blue-100 text-blue-700 text-xs px-1.5 py-0.5">
                                        <Zap className="h-2.5 w-2.5" />
                                      </Badge>
                                    )}
                                  </div>
                                </div>
                                <p className="text-xs text-slate-500 mb-2 line-clamp-1">{plantilla.asunto}</p>
                                <div className="flex gap-1 flex-wrap">
                                  <Button 
                                    variant="outline" 
                                    size="sm" 
                                    className="h-7 text-xs"
                                    onClick={() => handleOpenPreview(plantilla)}
                                  >
                                    <Eye className="h-3 w-3 mr-1" />
                                    Ver
                                  </Button>
                                  {isAdmin && (
                                    <>
                                      <Button 
                                        variant="outline" 
                                        size="sm" 
                                        className="h-7 text-xs"
                                        onClick={() => handleEdit(plantilla)}
                                      >
                                        <Edit className="h-3 w-3 mr-1" />
                                        Editar
                                      </Button>
                                      <Button 
                                        variant="outline" 
                                        size="sm" 
                                        className="h-7 text-xs text-green-600"
                                        onClick={() => handleOpenSendDialog(plantilla)}
                                      >
                                        <Send className="h-3 w-3" />
                                      </Button>
                                      <Button 
                                        variant="outline" 
                                        size="sm" 
                                        className="h-7 text-xs"
                                        onClick={() => handleDuplicate(plantilla)}
                                      >
                                        <Copy className="h-3 w-3" />
                                      </Button>
                                      <Button 
                                        variant="outline" 
                                        size="sm" 
                                        className="h-7 text-xs text-red-500 hover:text-red-700 hover:bg-red-50"
                                        onClick={() => handleDelete(plantilla)}
                                        data-testid={`delete-plantilla-${plantilla.id}`}
                                      >
                                        <Trash2 className="h-3 w-3" />
                                      </Button>
                                    </>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      )}
                    </Card>
                  ))}
                </div>
              ) : (
                /* Vista Cuadrícula Original */
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredPlantillas.map(plantilla => (
                    <Card 
                      key={plantilla.id} 
                      className={`dark:bg-slate-800 dark:border-slate-700 transition-all hover:shadow-md ${
                        plantilla.activa === false ? 'opacity-60' : ''
                      }`}
                    >
                      <CardHeader className="pb-2">
                        <div className="flex justify-between items-start gap-1 flex-wrap">
                          <Badge className={getCategoriaColor(plantilla.categoria)}>
                            {getCategoriaLabel(plantilla.categoria)}
                          </Badge>
                          <div className="flex gap-1 flex-wrap">
                            {plantilla.activa !== false ? (
                              <Badge className="bg-green-100 text-green-700 hover:bg-green-100">
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Activa
                              </Badge>
                            ) : (
                              <Badge variant="secondary" className="bg-gray-100 text-gray-500">
                                Inactiva
                              </Badge>
                            )}
                            {plantilla.configuracion?.envio_automatico && (
                              <Badge className="bg-blue-100 text-blue-700 hover:bg-blue-100">
                                <Zap className="h-3 w-3 mr-1" />
                                Automático
                              </Badge>
                            )}
                          </div>
                        </div>
                        <CardTitle className="text-lg mt-2">{plantilla.nombre}</CardTitle>
                        <CardDescription className="text-sm">
                          {plantilla.asunto}
                        </CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className="flex flex-wrap gap-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => handleOpenPreview(plantilla)}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            Ver
                          </Button>
                          {isAdmin && (
                            <>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                onClick={() => handleOpenSendDialog(plantilla)}
                              >
                                <Send className="h-3 w-3 mr-1" />
                                Enviar
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => handleEdit(plantilla)}>
                                <Edit className="h-3 w-3 mr-1" />
                                Editar
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => handleDuplicate(plantilla)}>
                                <Copy className="h-3 w-3" />
                              </Button>
                              <Button 
                                variant="outline" 
                                size="sm" 
                                className="text-red-500 hover:text-red-700"
                                onClick={() => handleDelete(plantilla)}
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Editor Dialog */}
        <Dialog open={showEditor} onOpenChange={setShowEditor}>
          <DialogContent className="max-w-7xl max-h-[95vh] h-[90vh] flex flex-col dark:bg-slate-800">
            <DialogHeader className="flex-shrink-0">
              <DialogTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                {editingPlantilla ? "Editar Plantilla" : "Nueva Plantilla"}
              </DialogTitle>
            </DialogHeader>
            
            <div className="flex-1 overflow-auto min-h-0">
              <div className="flex gap-4 h-full">
                {/* Panel Principal del Editor */}
                <div className="flex-1 flex flex-col overflow-y-auto pr-2">
                  <div className="grid grid-cols-2 gap-4 flex-shrink-0">
                    <div>
                      <Label>Nombre de la plantilla *</Label>
                      <Input
                        value={formData.nombre}
                        onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                        placeholder="Ej: Notificación de nuevo ticket"
                      />
                    </div>
                    <div>
                      <Label>Categoría</Label>
                      <Select 
                        value={formData.categoria} 
                        onValueChange={(v) => setFormData({ ...formData, categoria: v })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CATEGORIAS.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="mt-3 flex-shrink-0">
                    <Label>Asunto del correo *</Label>
                    <Input
                      value={formData.asunto}
                      onChange={(e) => setFormData({ ...formData, asunto: e.target.value })}
                      placeholder="Ej: Se te ha asignado el ticket {{ticket.id}}"
                    />
                  </div>

                  <div className="mt-3 flex-shrink-0">
                    <Label>Nombre del Remitente (opcional)</Label>
                    <Input
                      value={formData.nombre_remitente}
                      onChange={(e) => setFormData({ ...formData, nombre_remitente: e.target.value })}
                      placeholder="Ej: Recursos Humanos, Marketing QA"
                      data-testid="input-nombre-remitente"
                    />
                    <p className="text-xs text-slate-500 mt-1">Si se deja vacío, se usa el nombre configurado en SMTP</p>
                  </div>

                  <div className="mt-3 flex-1 flex flex-col min-h-0">
                    <div className="flex items-center justify-between mb-2 flex-shrink-0">
                      <Label>{editorTab === "config" ? "Configuración de Envío" : "Contenido HTML *"}</Label>
                      <div className="flex gap-1 bg-slate-100 rounded-lg p-1">
                        {getConfig().evento === "vacaciones_inicio" && (
                          <button
                            type="button"
                            onClick={() => setEditorTab("visual")}
                            className={`text-xs px-3 py-1.5 rounded-md flex items-center gap-1 transition-all ${
                              editorTab === "visual" ? "bg-white shadow-sm" : "hover:bg-white/50"
                            }`}
                          >
                            <Paintbrush className="h-3 w-3" />
                            Editor Visual
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => setEditorTab("editor")}
                          className={`text-xs px-3 py-1.5 rounded-md flex items-center gap-1 transition-all ${
                            editorTab === "editor" ? "bg-white shadow-sm" : "hover:bg-white/50"
                          }`}
                        >
                          <Code className="h-3 w-3" />
                          Editor
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditorTab("preview")}
                          className={`text-xs px-3 py-1.5 rounded-md flex items-center gap-1 transition-all ${
                            editorTab === "preview" ? "bg-white shadow-sm" : "hover:bg-white/50"
                          }`}
                        >
                          <Eye className="h-3 w-3" />
                          Vista Previa
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditorTab("config")}
                          className={`text-xs px-3 py-1.5 rounded-md flex items-center gap-1 transition-all ${
                            editorTab === "config" ? "bg-white shadow-sm" : "hover:bg-white/50"
                          }`}
                        >
                          <Settings className="h-3 w-3" />
                          Configuración
                        </button>
                        {getConfig().evento === "vacaciones_inicio" && editingPlantilla?.id && (
                          <button
                            type="button"
                            onClick={() => { setEditorTab("auditoria"); fetchAuditoriaVacaciones(editingPlantilla.id); }}
                            className={`text-xs px-3 py-1.5 rounded-md flex items-center gap-1 transition-all ${
                              editorTab === "auditoria" ? "bg-white shadow-sm" : "hover:bg-white/50"
                            }`}
                            data-testid="tab-auditoria-vacaciones"
                          >
                            <Mail className="h-3 w-3" />
                            Historial y Proyección
                          </button>
                        )}
                      </div>
                    </div>
                    
                    {/* Editor de contenido - Ocupa todo el ancho */}
                    <div className="flex-1 flex flex-col min-h-0">
                      {editorTab === "visual" ? (
                        <VisualEmailEditor
                          formData={formData}
                          setFormData={setFormData}
                          getAuthHeaders={getAuthHeaders}
                        />
                      ) : editorTab === "editor" ? (
                        <Textarea
                          id="contenido-editor"
                          value={formData.contenido}
                          onChange={(e) => setFormData({ ...formData, contenido: e.target.value })}
                          placeholder="<div>Contenido del correo...</div>"
                          className="font-mono text-sm flex-1 min-h-[400px] resize-none w-full"
                        />
                      ) : editorTab === "preview" ? (
                        <div className="bg-gray-100 rounded-lg border flex-1 min-h-[400px] overflow-y-auto p-4">
                          {editingPlantilla?.id ? (
                            <div>
                              <div className="flex items-center justify-between mb-3">
                                <p className="text-sm text-slate-600">Vista previa renderizada del correo</p>
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  onClick={() => handleOpenPreview(editingPlantilla)}
                                  className="h-7 text-xs"
                                >
                                  <Eye className="h-3 w-3 mr-1" />
                                  Abrir Vista Previa Completa
                                </Button>
                              </div>
                              <div className="bg-white rounded shadow-sm max-w-[600px] mx-auto">
                                <div 
                                  className="prose max-w-none prose-sm"
                                  dangerouslySetInnerHTML={{ __html: formData.contenido || "<p class='text-slate-400'>Sin contenido</p>" }}
                                />
                              </div>
                            </div>
                          ) : (
                            <div className="bg-white rounded shadow-sm max-w-[600px] mx-auto">
                              <div 
                                className="prose max-w-none prose-sm"
                                dangerouslySetInnerHTML={{ __html: formData.contenido || "<p class='text-slate-400'>Guarde primero la plantilla para acceder a la vista previa completa</p>" }}
                              />
                            </div>
                          )}
                        </div>
                      ) : editorTab === "config" ? (
                        /* Pestaña de Configuración */
                        <div className="bg-white rounded-lg border p-4 flex-1 min-h-[400px] overflow-y-auto space-y-4">
                          {/* Envío Automático Toggle */}
                          <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div>
                              <p className="font-medium text-blue-800">Envío Automático</p>
                              <p className="text-xs text-blue-600">Activar para enviar automáticamente cuando ocurra el evento</p>
                            </div>
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input 
                                type="checkbox" 
                                className="sr-only peer"
                                checked={getConfig().envio_automatico || false}
                                onChange={(e) => updateConfig({ envio_automatico: e.target.checked })}
                              />
                              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                            </label>
                          </div>

                          {/* Módulo */}
                          <div>
                            <Label className="mb-2 block">Módulo</Label>
                            <Select 
                              value={getConfig().modulo || "_none"} 
                              onValueChange={(v) => updateConfig({ modulo: v === "_none" ? "" : v, evento: "" })}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder="Seleccione un módulo" />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="_none">Sin módulo</SelectItem>
                                {Object.entries(modulosEventos.modulos || {}).map(([id, mod]) => (
                                  <SelectItem key={id} value={id}>{mod.label}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Evento Disparador - siempre visible */}
                          <div>
                            <Label className="mb-2 block">Evento Disparador (Detonador)</Label>
                            {(!getConfig().modulo || getConfig().modulo === "_none") ? (
                              <p className="text-sm text-amber-600 bg-amber-50 p-2 rounded border border-amber-200">
                                Seleccione primero un módulo para ver los eventos disponibles
                              </p>
                            ) : (
                              <Select 
                                value={getConfig().evento || "_none"} 
                                onValueChange={(v) => updateConfig({ evento: v === "_none" ? "" : v })}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Seleccione un evento" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="_none">Sin evento</SelectItem>
                                  {(modulosEventos.modulos?.[getConfig().modulo]?.eventos || []).map(ev => (
                                    <SelectItem key={ev.id} value={ev.id}>{ev.label}</SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                            )}
                          </div>

                          {/* Hora de Envío - visible para eventos programados por fecha */}
                          {getConfig().evento === "vacaciones_inicio" && (
                            <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                              <Label className="mb-2 block text-blue-800 font-semibold">Hora de Envío Programado</Label>
                              <p className="text-xs text-blue-600 mb-2">
                                El correo se enviará automáticamente en la fecha de inicio de vacaciones del empleado, a la hora que configure aquí.
                              </p>
                              <input 
                                type="time"
                                data-testid="input-hora-envio"
                                className="w-full px-3 py-2 border rounded-md text-sm bg-white"
                                value={getConfig().hora_envio || "08:00"}
                                onChange={(e) => updateConfig({ hora_envio: e.target.value })}
                              />
                              <p className="text-xs text-blue-500 mt-1">
                                Ejemplo: Si las vacaciones inician el 16 de marzo y configura las 08:00, el correo se enviará el 16 de marzo a las 8:00 AM.
                              </p>
                            </div>
                          )}

                          {getConfig().evento === "vacaciones_inicio" && (
                            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                              <Label className="mb-2 block text-amber-800 font-semibold">Mínimo de Días de Vacaciones</Label>
                              <p className="text-xs text-amber-600 mb-2">
                                El correo solo se enviará si las vacaciones son de al menos esta cantidad de días. Si se deja en 0 o vacío, se envía siempre.
                              </p>
                              <Input
                                type="number"
                                min="0"
                                data-testid="input-minimo-dias-vacaciones"
                                className="bg-white"
                                placeholder="Ej: 5"
                                value={getConfig().minimo_dias_vacaciones || ""}
                                onChange={(e) => updateConfig({ minimo_dias_vacaciones: parseInt(e.target.value) || 0 })}
                              />
                              <p className="text-xs text-amber-500 mt-1">
                                Ejemplo: Si configura 5, solo se enviará el correo para vacaciones de 5 días o más.
                              </p>
                            </div>
                          )}

                          {/* Destinatarios */}
                          <div>
                            <Label className="mb-2 block">Destinatarios</Label>
                            <div className="grid grid-cols-2 gap-2">
                              {(modulosEventos.destinatarios || []).map(dest => (
                                <label 
                                  key={dest.id} 
                                  className={`flex items-center gap-2 p-2 rounded border cursor-pointer transition-all ${
                                    (getConfig().destinatarios || []).includes(dest.id)
                                      ? 'bg-blue-50 border-blue-300'
                                      : 'bg-white border-gray-200 hover:bg-gray-50'
                                  }`}
                                >
                                  <Checkbox 
                                    checked={(getConfig().destinatarios || []).includes(dest.id)}
                                    onCheckedChange={(checked) => {
                                      const current = getConfig().destinatarios || [];
                                      const updated = checked 
                                        ? [...current, dest.id]
                                        : current.filter(d => d !== dest.id);
                                      updateConfig({ destinatarios: updated });
                                    }}
                                  />
                                  <span className="text-sm">{dest.label}</span>
                                </label>
                              ))}
                            </div>
                          </div>

                          {/* Emails Adicionales */}
                          <div>
                            <Label className="mb-2 block">Emails Adicionales</Label>
                            <div className="flex gap-2 mb-2">
                              <Input
                                type="email"
                                placeholder="correo@ejemplo.com"
                                value={emailInput}
                                onChange={(e) => setEmailInput(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' && emailInput.trim()) {
                                    e.preventDefault();
                                    const emails = getConfig().emails_adicionales || [];
                                    if (!emails.includes(emailInput.trim())) {
                                      updateConfig({ emails_adicionales: [...emails, emailInput.trim()] });
                                    }
                                    setEmailInput("");
                                  }
                                }}
                              />
                              <Button 
                                type="button"
                                variant="outline"
                                onClick={() => {
                                  if (emailInput.trim()) {
                                    const emails = getConfig().emails_adicionales || [];
                                    if (!emails.includes(emailInput.trim())) {
                                      updateConfig({ emails_adicionales: [...emails, emailInput.trim()] });
                                    }
                                    setEmailInput("");
                                  }
                                }}
                              >
                                <Plus className="h-4 w-4" />
                              </Button>
                            </div>
                            {(getConfig().emails_adicionales || []).length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {(getConfig().emails_adicionales || []).map((email, idx) => (
                                  <Badge key={idx} variant="secondary" className="flex items-center gap-1">
                                    {email}
                                    <X 
                                      className="h-3 w-3 cursor-pointer hover:text-red-500"
                                      onClick={() => {
                                        const emails = getConfig().emails_adicionales || [];
                                        updateConfig({ emails_adicionales: emails.filter((_, i) => i !== idx) });
                                      }}
                                    />
                                  </Badge>
                                ))}
                              </div>
                            )}
                          </div>

                          {/* Usuarios Específicos (selector con checkboxes) */}
                          <div data-testid="usuarios-especificos-selector">
                            <Label className="mb-2 block">Usuarios específicos</Label>
                            <p className="text-xs text-gray-500 mb-2">Selecciona personas concretas que recibirán este correo (además de los destinatarios de arriba).</p>
                            <Input
                              placeholder="Buscar usuario por nombre, correo o departamento..."
                              value={configUserSearch}
                              onChange={(e) => setConfigUserSearch(e.target.value)}
                              className="mb-2"
                            />
                            <div className="max-h-48 overflow-y-auto border rounded-md divide-y">
                              {usuarios
                                .filter(u => {
                                  const t = configUserSearch.toLowerCase();
                                  return !t || u.name?.toLowerCase().includes(t) || u.email?.toLowerCase().includes(t) || u.departamento_nombre?.toLowerCase().includes(t);
                                })
                                .slice(0, 100)
                                .map(u => {
                                  const sel = (getConfig().destinatarios_usuarios || []).includes(u.id);
                                  return (
                                    <label key={u.id} className={`flex items-center gap-2 p-2 cursor-pointer ${sel ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                                      <Checkbox
                                        checked={sel}
                                        onCheckedChange={(checked) => {
                                          const current = getConfig().destinatarios_usuarios || [];
                                          const updated = checked ? [...current, u.id] : current.filter(id => id !== u.id);
                                          updateConfig({ destinatarios_usuarios: updated });
                                        }}
                                      />
                                      <span className="text-sm flex-1">{u.name} <span className="text-gray-400">· {u.email}</span></span>
                                    </label>
                                  );
                                })}
                              {usuarios.length === 0 && (
                                <p className="text-xs text-gray-400 p-2">Cargando usuarios…</p>
                              )}
                            </div>
                            {(getConfig().destinatarios_usuarios || []).length > 0 && (
                              <div className="flex flex-wrap gap-2 mt-2">
                                {(getConfig().destinatarios_usuarios || []).map(uid => {
                                  const u = usuarios.find(x => x.id === uid);
                                  return (
                                    <Badge key={uid} variant="secondary" className="flex items-center gap-1">
                                      {u?.name || uid}
                                      <X
                                        className="h-3 w-3 cursor-pointer hover:text-red-500"
                                        onClick={() => {
                                          const current = getConfig().destinatarios_usuarios || [];
                                          updateConfig({ destinatarios_usuarios: current.filter(id => id !== uid) });
                                        }}
                                      />
                                    </Badge>
                                  );
                                })}
                              </div>
                            )}
                          </div>

                          {/* Configuración especial para alertas de días pendientes */}
                          {(getConfig().evento === "vacaciones_dias_pendientes" || getConfig().evento === "permiso_dias_pendientes") && (
                            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 space-y-4">
                              <div className="flex items-center gap-2">
                                <AlertTriangle className="h-5 w-5 text-amber-600" />
                                <Label className="font-medium text-amber-800">Configuración de Alerta de Días Pendientes</Label>
                              </div>
                              
                              <div>
                                <Label className="mb-2 block text-sm text-amber-700">
                                  Umbral de días para disparar alerta
                                </Label>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    min="1"
                                    max="30"
                                    value={getConfig().umbral_dias || 5}
                                    onChange={(e) => updateConfig({ umbral_dias: parseInt(e.target.value) || 5 })}
                                    className="w-24"
                                  />
                                  <span className="text-sm text-amber-700">días o menos</span>
                                </div>
                                <p className="text-xs text-amber-600 mt-1">
                                  El correo se enviará cuando al usuario le queden esta cantidad de días o menos
                                </p>
                              </div>
                            </div>
                          )}

                          {(getConfig().evento === "ticket_sla_por_vencer" || getConfig().evento === "ticket_sla_vencido") && (
                            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 space-y-4" data-testid="sla-dias-config">
                              <div className="flex items-center gap-2">
                                <AlertTriangle className="h-5 w-5 text-amber-600" />
                                <Label className="font-medium text-amber-800">Configuración del detonador SLA</Label>
                              </div>
                              <div>
                                <Label className="mb-2 block text-sm text-amber-700">
                                  {getConfig().evento === "ticket_sla_por_vencer"
                                    ? "Avisar cuántos días ANTES del vencimiento del SLA"
                                    : "Empezar a avisar a partir de cuántos días DESPUÉS del vencimiento del SLA"}
                                </Label>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    min="0"
                                    max="60"
                                    value={getConfig().sla_dias ?? 1}
                                    onChange={(e) => updateConfig({ sla_dias: parseInt(e.target.value) || 0 })}
                                    className="w-24"
                                    data-testid="sla-dias-input"
                                  />
                                  <span className="text-sm text-amber-700">
                                    {getConfig().evento === "ticket_sla_por_vencer" ? "días antes" : "días después"}
                                  </span>
                                </div>
                                <p className="text-xs text-amber-600 mt-1">
                                  {getConfig().evento === "ticket_sla_por_vencer"
                                    ? "El correo se enviará una vez cuando falte esta cantidad de días (o menos) para el vencimiento. Usa la variable {dias_para_vencer}."
                                    : "El correo se enviará DIARIAMENTE mientras el ticket siga vencido, comenzando cuando lleve esta cantidad de días vencido. Usa la variable {dias_vencido}."}
                                </p>
                              </div>
                            </div>
                          )}

                          {getConfig().evento === "conversacion_larga" && (
                            <div className="p-4 bg-amber-50 rounded-lg border border-amber-200 space-y-4" data-testid="conversacion-larga-config">
                              <div className="flex items-center gap-2">
                                <AlertTriangle className="h-5 w-5 text-amber-600" />
                                <Label className="font-medium text-amber-800">Configuración del detonador</Label>
                              </div>
                              <div>
                                <Label className="mb-2 block text-sm text-amber-700">Alertar cuando la conversación lleve asignada más de</Label>
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    min="1"
                                    max="720"
                                    value={getConfig().horas_limite ?? 24}
                                    onChange={(e) => updateConfig({ horas_limite: parseInt(e.target.value) || 24 })}
                                    className="w-24"
                                    data-testid="conversacion-larga-horas-input"
                                  />
                                  <span className="text-sm text-amber-700">horas sin concluirse</span>
                                </div>
                                <p className="text-xs text-amber-600 mt-1">
                                  El sistema revisa cada 15 minutos y alerta UNA VEZ por asignación al supervisor del ejecutivo asignado (correo + notificación). Si la conversación se transfiere, el contador se reinicia. Usa las variables {"{cliente}"}, {"{ejecutivo}"}, {"{horas}"} y {"{fecha_asignacion}"}.
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Opción de incluir firmas digitales */}
                          {getConfig().modulo === "gestion_humana" && (
                            <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border">
                              <Checkbox 
                                id="incluir_firmas"
                                checked={getConfig().incluir_firmas || false}
                                onCheckedChange={(checked) => updateConfig({ incluir_firmas: checked })}
                              />
                              <div>
                                <Label htmlFor="incluir_firmas" className="cursor-pointer">Incluir firmas digitales</Label>
                                <p className="text-xs text-slate-500">
                                  Incluye las firmas del empleado, supervisor y aprobador en el correo
                                </p>
                              </div>
                            </div>
                          )}

                          {/* Resumen de Configuración */}
                          {getConfig().envio_automatico && getConfig().modulo && getConfig().modulo !== "_none" && getConfig().evento && getConfig().evento !== "_none" && (
                            <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                              <p className="font-medium text-green-800 mb-1">Resumen de Configuración</p>
                              {getConfig().evento === "vacaciones_inicio" ? (
                                <p className="text-sm text-green-700">
                                  Esta plantilla se enviará automáticamente <strong>en la fecha de inicio de vacaciones</strong> del empleado, a las <strong>{getConfig().hora_envio || "08:00"}</strong> hrs.
                                  {getConfig().minimo_dias_vacaciones > 0 && (
                                    <> Solo se enviará si las vacaciones son de <strong>{getConfig().minimo_dias_vacaciones} días o más</strong>.</>
                                  )}
                                  {" "}El sistema calculará automáticamente la fecha de reintegro laboral (excluyendo fines de semana y días feriados).
                                </p>
                              ) : (
                                <p className="text-sm text-green-700">
                                  Esta plantilla se enviará automáticamente cuando ocurra el evento "
                                  <strong>{modulosEventos.modulos?.[getConfig().modulo]?.eventos?.find(e => e.id === getConfig().evento)?.label}</strong>
                                  " en el módulo "
                                  <strong>{modulosEventos.modulos?.[getConfig().modulo]?.label}</strong>
                                  ".
                                </p>
                              )}
                              {(getConfig().destinatarios?.length > 0 || getConfig().emails_adicionales?.length > 0) && (
                                <p className="text-sm text-green-700 mt-1">
                                  Destinatarios: {[
                                    ...(getConfig().destinatarios || []).map(d => 
                                      modulosEventos.destinatarios?.find(x => x.id === d)?.label
                                    ),
                                    ...(getConfig().emails_adicionales || [])
                                  ].filter(Boolean).join(", ")}
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      ) : editorTab === "auditoria" ? (
                        /* Pestaña: Historial de envíos pasados + Proyección futura */
                        <div className="bg-white rounded-lg border p-4 flex-1 min-h-[400px] overflow-y-auto space-y-4" data-testid="auditoria-vacaciones-content">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="font-semibold text-slate-800 flex items-center gap-2">
                                <Mail className="h-4 w-4 text-blue-600" />
                                Auditoría de envíos — Personal en Vacaciones
                              </h3>
                              <p className="text-xs text-slate-500 mt-1">
                                Últimos 90 días + próximos 60 días. Filtro de mínimo: <strong>{auditoriaData?.minimo_dias_vacaciones || 0} días</strong>
                              </p>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => fetchAuditoriaVacaciones(editingPlantilla.id)}
                              disabled={auditoriaLoading}
                              data-testid="refresh-auditoria-btn"
                            >
                              {auditoriaLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
                              <span className="ml-1 text-xs">Actualizar</span>
                            </Button>
                          </div>

                          {auditoriaLoading && !auditoriaData ? (
                            <div className="text-center py-12">
                              <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
                              <p className="text-sm text-slate-500 mt-2">Cargando auditoría...</p>
                            </div>
                          ) : !auditoriaData ? (
                            <div className="text-center py-12 text-slate-400 text-sm">Hacé clic en "Actualizar" para cargar la auditoría.</div>
                          ) : (
                            <>
                              {/* Resumen cards */}
                              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                                <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg">
                                  <p className="text-xs text-emerald-700">Enviados</p>
                                  <p className="text-2xl font-bold text-emerald-800">{auditoriaData.resumen.enviados}</p>
                                </div>
                                <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg">
                                  <p className="text-xs text-rose-700">No enviados (pasados)</p>
                                  <p className="text-2xl font-bold text-rose-800">{auditoriaData.resumen.no_enviados_pasados}</p>
                                </div>
                                <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg">
                                  <p className="text-xs text-slate-600">Omitidos por mínimo (pasados)</p>
                                  <p className="text-2xl font-bold text-slate-700">{auditoriaData.resumen.omitidos_por_minimo_pasados}</p>
                                </div>
                                <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                                  <p className="text-xs text-blue-700">A enviar (próx. 60d)</p>
                                  <p className="text-2xl font-bold text-blue-800">{auditoriaData.resumen.proyectados_a_enviar}</p>
                                </div>
                                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                  <p className="text-xs text-amber-700">Serán omitidos (próx. 60d)</p>
                                  <p className="text-2xl font-bold text-amber-800">{auditoriaData.resumen.proyectados_a_omitir}</p>
                                </div>
                              </div>

                              {/* Advertencias */}
                              {!auditoriaData.activa && (
                                <div className="p-2 bg-rose-50 border border-rose-200 rounded text-sm text-rose-700">
                                  ⚠️ La plantilla está <strong>INACTIVA</strong>. Aunque haya vacaciones que cumplan el filtro, no se enviarán correos hasta activarla.
                                </div>
                              )}
                              {!auditoriaData.envio_automatico && (
                                <div className="p-2 bg-amber-50 border border-amber-200 rounded text-sm text-amber-700">
                                  ⚠️ <strong>Envío automático desactivado</strong>. Activalo en la pestaña Configuración para que el scheduler dispare estos correos.
                                </div>
                              )}

                              {/* Estado del Scheduler / Detonador */}
                              {schedulerStatus && (
                                <div className="p-3 bg-indigo-50 border border-indigo-200 rounded-lg space-y-1.5">
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                      <span className={`inline-block w-2 h-2 rounded-full ${schedulerStatus.registrado && schedulerStatus.corriendo ? "bg-emerald-500 animate-pulse" : "bg-rose-500"}`}></span>
                                      <span className="font-semibold text-sm text-indigo-800">
                                        Detonador (Scheduler): {schedulerStatus.registrado && schedulerStatus.corriendo ? "🟢 ACTIVO" : "🔴 INACTIVO"}
                                      </span>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="outline"
                                      onClick={handleDispararManual}
                                      disabled={triggeringManual || !auditoriaData.activa}
                                      className="border-indigo-400 text-indigo-700 hover:bg-indigo-100"
                                      data-testid="disparar-scheduler-manual-btn"
                                      title={!auditoriaData.activa ? "Activá la plantilla primero" : "Ejecuta el scheduler AHORA, ignorando la ventana horaria. Procesa solo vacaciones que inicien hoy."}
                                    >
                                      {triggeringManual ? <Loader2 className="h-3 w-3 animate-spin" /> : <Mail className="h-3 w-3" />}
                                      <span className="ml-1 text-xs">Disparar ahora (test)</span>
                                    </Button>
                                  </div>
                                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                                    <div>
                                      <span className="text-indigo-600">Intervalo:</span>{" "}
                                      <strong>{schedulerStatus.intervalo_minutos ? `cada ${schedulerStatus.intervalo_minutos} min` : "—"}</strong>
                                    </div>
                                    <div>
                                      <span className="text-indigo-600">Próximo run:</span>{" "}
                                      <strong>{schedulerStatus.proximo_run ? new Date(schedulerStatus.proximo_run).toLocaleString("es-DO") : "—"}</strong>
                                    </div>
                                    <div>
                                      <span className="text-indigo-600">Hora envío configurada:</span>{" "}
                                      <strong>{auditoriaData.hora_envio || "—"} (DR)</strong>
                                    </div>
                                    <div>
                                      <span className="text-indigo-600">Últimos envíos registrados:</span>{" "}
                                      <strong>{schedulerStatus.ultimos_envios?.length || 0}</strong>
                                    </div>
                                  </div>
                                  {schedulerStatus.ultimos_envios && schedulerStatus.ultimos_envios.length > 0 && (
                                    <details className="text-xs">
                                      <summary className="cursor-pointer text-indigo-700 hover:underline">Ver últimos {schedulerStatus.ultimos_envios.length} envíos del scheduler (todas las plantillas)</summary>
                                      <ul className="mt-1 space-y-0.5 pl-3 text-slate-600">
                                        {schedulerStatus.ultimos_envios.map((e, i) => (
                                          <li key={i} className="font-mono">
                                            • {new Date(e.sent_at).toLocaleString("es-DO")} — solicitud {e.solicitud_id?.slice(0, 8)}... ({e.dias_solicitados || "?"}d)
                                          </li>
                                        ))}
                                      </ul>
                                    </details>
                                  )}
                                  {(!schedulerStatus.registrado || !schedulerStatus.corriendo) && (
                                    <div className="text-xs text-rose-700 mt-1">
                                      ⚠️ El detonador no está activo. Contactá a Emergent Support — puede ser que el backend deba reiniciarse.
                                    </div>
                                  )}
                                </div>
                              )}

                              {/* Sección PASADAS */}
                              <div>
                                <h4 className="font-semibold text-sm mb-2 text-slate-700">📋 Vacaciones iniciadas (últimos 90 días) — {auditoriaData.pasadas.length}</h4>
                                {auditoriaData.pasadas.length === 0 ? (
                                  <p className="text-xs text-slate-400 italic px-2">No hay vacaciones aprobadas que iniciaron en los últimos 90 días.</p>
                                ) : (
                                  <div className="border rounded-lg overflow-hidden">
                                    <table className="w-full text-xs">
                                      <thead className="bg-slate-100 text-slate-600">
                                        <tr>
                                          <th className="px-2 py-2 text-left">Empleado</th>
                                          <th className="px-2 py-2 text-left">Depto</th>
                                          <th className="px-2 py-2 text-center">Inicio</th>
                                          <th className="px-2 py-2 text-center">Fin</th>
                                          <th className="px-2 py-2 text-center">Días</th>
                                          <th className="px-2 py-2 text-center">Estado</th>
                                          <th className="px-2 py-2 text-center">Enviado el</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {auditoriaData.pasadas.map((item) => (
                                          <tr key={item.solicitud_id} className="border-t hover:bg-slate-50">
                                            <td className="px-2 py-1.5">{item.empleado_nombre}</td>
                                            <td className="px-2 py-1.5 text-slate-500">{item.departamento}</td>
                                            <td className="px-2 py-1.5 text-center">{item.fecha_inicio}</td>
                                            <td className="px-2 py-1.5 text-center">{item.fecha_fin}</td>
                                            <td className="px-2 py-1.5 text-center font-mono font-semibold">{item.dias_solicitados}</td>
                                            <td className="px-2 py-1.5 text-center">
                                              {item.estado === "enviado" && <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">✅ Enviado</span>}
                                              {item.estado === "no_enviado" && (
                                                <span
                                                  className="px-2 py-0.5 bg-rose-100 text-rose-700 rounded cursor-help underline decoration-dotted"
                                                  title={item.motivo_no_enviado || "Sin causa identificada"}
                                                >❌ NO enviado</span>
                                              )}
                                              {item.estado === "omitido_por_minimo" && (
                                                <span
                                                  className="px-2 py-0.5 bg-slate-200 text-slate-600 rounded cursor-help underline decoration-dotted"
                                                  title={item.motivo_no_enviado || `La vacación es menor al mínimo de ${auditoriaData.minimo_dias_vacaciones} días.`}
                                                >⏭️ Omitido (&lt;{auditoriaData.minimo_dias_vacaciones}d)</span>
                                              )}
                                            </td>
                                            <td className="px-2 py-1.5 text-center text-slate-500">{item.sent_at ? new Date(item.sent_at).toLocaleString("es-DO") : "—"}</td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>

                              {/* Sección FUTURAS */}
                              <div>
                                <h4 className="font-semibold text-sm mb-2 text-slate-700">🔮 Proyección (próximos 60 días) — {auditoriaData.futuras.length}</h4>
                                {auditoriaData.futuras.length === 0 ? (
                                  <p className="text-xs text-slate-400 italic px-2">No hay vacaciones aprobadas que inicien en los próximos 60 días.</p>
                                ) : (
                                  <div className="border rounded-lg overflow-hidden">
                                    <table className="w-full text-xs">
                                      <thead className="bg-blue-50 text-blue-700">
                                        <tr>
                                          <th className="px-2 py-2 text-left">Empleado</th>
                                          <th className="px-2 py-2 text-left">Depto</th>
                                          <th className="px-2 py-2 text-center">Inicio</th>
                                          <th className="px-2 py-2 text-center">Fin</th>
                                          <th className="px-2 py-2 text-center">Días</th>
                                          <th className="px-2 py-2 text-center">Proyección</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {auditoriaData.futuras.map((item) => (
                                          <tr key={item.solicitud_id} className="border-t hover:bg-slate-50">
                                            <td className="px-2 py-1.5">{item.empleado_nombre}</td>
                                            <td className="px-2 py-1.5 text-slate-500">{item.departamento}</td>
                                            <td className="px-2 py-1.5 text-center">{item.fecha_inicio}</td>
                                            <td className="px-2 py-1.5 text-center">{item.fecha_fin}</td>
                                            <td className="px-2 py-1.5 text-center font-mono font-semibold">{item.dias_solicitados}</td>
                                            <td className="px-2 py-1.5 text-center">
                                              {item.estado === "pendiente_envio" && (
                                                <span
                                                  className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded cursor-help underline decoration-dotted"
                                                  title={item.fecha_envio_programada ? `Se enviará el ${item.fecha_envio_programada}` : "Se enviará el día de inicio en la hora configurada"}
                                                >⏰ Se enviará</span>
                                              )}
                                              {item.estado === "sera_omitido" && (
                                                <span
                                                  className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded cursor-help underline decoration-dotted"
                                                  title={item.motivo_no_enviado || `Menor al mínimo configurado.`}
                                                >⏭️ Será omitido (&lt;{auditoriaData.minimo_dias_vacaciones}d)</span>
                                              )}
                                              {item.estado === "enviado" && <span className="px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded">✅ Enviado</span>}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            </>
                          )}
                        </div>
                      ) : (
                        <div className="p-4 text-slate-400 text-sm">Pestaña desconocida</div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Panel Lateral de Variables */}
                <div className="w-72 border-l pl-4 flex flex-col overflow-hidden">
                  <div className="flex items-center gap-2 mb-3 pb-2 border-b flex-shrink-0">
                    <Variable className="h-4 w-4 text-blue-600" />
                    <Label className="text-blue-700 font-semibold">Variables del Sistema</Label>
                  </div>
                  <p className="text-xs text-slate-500 mb-2 flex-shrink-0">
                    Clic para insertar en el contenido
                  </p>
                  <div className="relative mb-2 flex-shrink-0">
                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-slate-400" />
                    <Input
                      placeholder="Buscar variable…"
                      value={varSearchEditor}
                      onChange={(e) => setVarSearchEditor(e.target.value)}
                      className="pl-7 h-8 text-xs"
                      data-testid="var-search-editor"
                    />
                  </div>
                  <ScrollArea className="flex-1 pr-2">
                    <Accordion type="multiple" defaultValue={["usuario", "sistema", "solicitud_gh", "saldo_dias", "firmas"]} className="w-full">
                      {Object.entries(filterCampos(varSearchEditor)).map(([key, grupo]) => (
                        <AccordionItem key={key} value={key} className="border-b-0">
                          <AccordionTrigger className="text-xs font-semibold py-2 hover:no-underline">
                            <span className="flex items-center gap-1">
                              <ChevronRight className="h-3 w-3" />
                              {grupo.label}
                            </span>
                          </AccordionTrigger>
                          <AccordionContent className="pb-2">
                            <div className="space-y-1">
                              {grupo.campos.map(campo => (
                                <button
                                  key={campo.id}
                                  onClick={() => insertVariable(campo.id)}
                                  className="w-full text-left p-2 rounded border border-transparent hover:bg-blue-50 hover:border-blue-200 transition-all text-xs group"
                                >
                                  <div className="flex items-center justify-between">
                                    <code className="text-blue-600 bg-blue-50 px-1 rounded text-[10px] group-hover:bg-blue-100">
                                      {`{{${campo.id}}}`}
                                    </code>
                                    <Plus className="h-3 w-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity" />
                                  </div>
                                  <p className="text-slate-600 mt-0.5 truncate">{campo.label}</p>
                                </button>
                              ))}
                            </div>
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </ScrollArea>
                </div>
              </div>
            </div>

            <DialogFooter className="mt-4 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowEditor(false)}>
                Cancelar
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700" 
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                {editingPlantilla ? "Guardar Cambios" : "Crear Plantilla"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Preview Dialog - Vista Previa Completa */}
        <Dialog open={showPreview} onOpenChange={setShowPreview}>
          <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col dark:bg-slate-800" data-testid="preview-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5 text-blue-600" />
                Vista Previa del Correo
              </DialogTitle>
              <DialogDescription>
                Así se verá el correo cuando sea enviado. Puede editar los valores de las variables y actualizar la vista previa.
              </DialogDescription>
            </DialogHeader>
            
            <div className="flex-1 overflow-hidden flex gap-4" style={{minHeight: "400px"}}>
              {/* Left panel: Variables */}
              {previewDetectedVars.length > 0 && (
                <div className="w-72 shrink-0 flex flex-col border rounded-lg bg-slate-50 dark:bg-slate-900">
                  <div className="p-3 border-b bg-slate-100 dark:bg-slate-800 rounded-t-lg">
                    <p className="font-semibold text-sm text-slate-700 dark:text-slate-300 flex items-center gap-2">
                      <Variable className="h-4 w-4" />
                      Variables ({previewDetectedVars.length})
                    </p>
                  </div>
                  <ScrollArea className="flex-1 p-3">
                    <div className="space-y-3">
                      {previewDetectedVars.map(varName => (
                        <div key={varName}>
                          <Label className="text-xs text-slate-500 mb-1 block font-mono">{`{{${varName}}}`}</Label>
                          <Input
                            value={previewVars[varName] || ""}
                            onChange={e => setPreviewVars(prev => ({...prev, [varName]: e.target.value}))}
                            className="h-8 text-sm"
                            data-testid={`preview-var-${varName}`}
                          />
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                  <div className="p-3 border-t">
                    <Button 
                      onClick={handleRefreshPreview} 
                      disabled={loadingPreview}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white h-9 text-sm"
                      data-testid="refresh-preview-btn"
                    >
                      {loadingPreview ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                      Actualizar Vista Previa
                    </Button>
                  </div>
                </div>
              )}

              {/* Right panel: Email preview */}
              <div className="flex-1 flex flex-col border rounded-lg overflow-hidden">
                {/* Email header simulation */}
                <div className="bg-white dark:bg-slate-700 border-b p-3 space-y-1">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500 w-12">De:</span>
                    <span className="font-medium text-slate-800 dark:text-slate-200">Sistema de Gestión Empresarial</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500 w-12">Para:</span>
                    <span className="text-slate-600 dark:text-slate-300">destinatario@ejemplo.com</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-slate-500 w-12">Asunto:</span>
                    <span className="font-semibold text-slate-900 dark:text-white">{previewAsunto || editingPlantilla?.asunto || "..."}</span>
                  </div>
                </div>
                
                {/* Email body */}
                <ScrollArea className="flex-1 bg-gray-100 dark:bg-slate-800">
                  <div className="p-4">
                    {loadingPreview ? (
                      <div className="flex items-center justify-center py-20">
                        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
                        <span className="ml-3 text-slate-500">Generando vista previa...</span>
                      </div>
                    ) : (
                      <div className="bg-white rounded shadow-sm max-w-[620px] mx-auto">
                        <div 
                          className="p-0"
                          dangerouslySetInnerHTML={{ __html: previewHtml || editingPlantilla?.contenido || "<p style='padding:20px;color:#999;'>Sin contenido</p>" }}
                          data-testid="preview-email-body"
                        />
                      </div>
                    )}
                  </div>
                </ScrollArea>
              </div>
            </div>

            <DialogFooter className="flex justify-between items-center">
              <Button variant="outline" onClick={() => setShowPreview(false)}>
                Cerrar
              </Button>
              {isAdmin && (
                <Button 
                  className="bg-blue-600 hover:bg-blue-700"
                  onClick={() => {
                    setShowPreview(false);
                    handleEdit(editingPlantilla);
                  }}
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Editar Plantilla
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Referencia de Campos Dialog */}
        <Dialog open={showCamposRef} onOpenChange={setShowCamposRef}>
          <DialogContent className="max-w-4xl max-h-[90vh] flex flex-col dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Code className="h-5 w-5 text-blue-600" />
                Variables del Sistema
              </DialogTitle>
              <DialogDescription>
                Copie estas variables para usarlas en sus plantillas de correo
              </DialogDescription>
            </DialogHeader>
            
            <div className="relative mb-3 flex-shrink-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Buscar variable por nombre o categoría…"
                value={varSearchRef}
                onChange={(e) => setVarSearchRef(e.target.value)}
                className="pl-10"
                data-testid="var-search-ref"
              />
            </div>
            
            <ScrollArea className="flex-1">
              <Accordion type="multiple" className="w-full" defaultValue={Object.keys(CAMPOS_SISTEMA)}>
                {Object.entries(filterCampos(varSearchRef)).map(([key, grupo]) => (
                  <AccordionItem key={key} value={key}>
                    <AccordionTrigger className="text-base font-medium">
                      {grupo.label}
                    </AccordionTrigger>
                    <AccordionContent>
                      <div className="space-y-2">
                        {grupo.campos.map(campo => (
                          <div 
                            key={campo.id}
                            className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-700 rounded-lg"
                          >
                            <div>
                              <p className="font-medium text-slate-800 dark:text-slate-200">{campo.label}</p>
                              <code className="text-sm text-blue-600 bg-blue-50 dark:bg-blue-900/30 px-2 py-0.5 rounded">
                                {`{{${campo.id}}}`}
                              </code>
                              <p className="text-xs text-slate-500 mt-1">Ejemplo: {campo.ejemplo}</p>
                            </div>
                            <Button 
                              variant="ghost" 
                              size="sm"
                              onClick={() => copyVariable(campo.id)}
                            >
                              <Clipboard className="h-4 w-4" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </ScrollArea>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCamposRef(false)}>
                Cerrar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Send Email Dialog */}
        <Dialog open={showSendDialog} onOpenChange={(open) => {
          if (!open) {
            setSendResult(null);
          }
          setShowSendDialog(open);
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] flex flex-col dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-green-600" />
                Enviar Correo: {sendingPlantilla?.nombre}
              </DialogTitle>
              <DialogDescription>
                Seleccione los usuarios a los que desea enviar este correo
              </DialogDescription>
            </DialogHeader>
            
            {sendResult ? (
              // Mostrar resultado del envío
              <div className="flex-1 overflow-y-auto">
                <div className="space-y-4 py-4">
                  <div className="flex gap-4 justify-center">
                    <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                      <CheckCircle className="h-8 w-8 text-green-600 mx-auto mb-2" />
                      <p className="text-2xl font-bold text-green-700">{sendResult.enviados}</p>
                      <p className="text-sm text-green-600">Enviados</p>
                    </div>
                    {sendResult.errores > 0 && (
                      <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                        <AlertTriangle className="h-8 w-8 text-red-600 mx-auto mb-2" />
                        <p className="text-2xl font-bold text-red-700">{sendResult.errores}</p>
                        <p className="text-sm text-red-600">Fallidos</p>
                      </div>
                    )}
                  </div>

                  {sendResult.detalle_enviados?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-green-700 mb-2 flex items-center gap-2">
                        <Check className="h-4 w-4" />
                        Correos enviados exitosamente:
                      </h4>
                      <div className="bg-green-50 rounded-lg p-3 max-h-40 overflow-y-auto">
                        {sendResult.detalle_enviados.map((item, idx) => (
                          <div key={idx} className="text-sm text-green-800 py-1 border-b border-green-100 last:border-0">
                            {item.name} ({item.email})
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {sendResult.detalle_errores?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-red-700 mb-2 flex items-center gap-2">
                        <X className="h-4 w-4" />
                        Errores en envío:
                      </h4>
                      <div className="bg-red-50 rounded-lg p-3 max-h-40 overflow-y-auto">
                        {sendResult.detalle_errores.map((item, idx) => (
                          <div key={idx} className="text-sm text-red-800 py-1 border-b border-red-100 last:border-0">
                            {item.user}: {item.error}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              // Mostrar selector de usuarios
              <div className="flex-1 flex flex-col min-h-0">
                {/* Buscador */}
                <div className="relative mb-3 flex-shrink-0">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Buscar por nombre, email o departamento..."
                    value={userSearchTerm}
                    onChange={(e) => setUserSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>

                {/* Info de selección */}
                <div className="flex justify-between items-center mb-2 flex-shrink-0">
                  <span className="text-sm text-slate-500">
                    {selectedUsers.length} usuario(s) seleccionado(s)
                  </span>
                  <Button 
                    variant="link" 
                    size="sm" 
                    onClick={handleSelectAll}
                    className="text-blue-600"
                  >
                    {selectedUsers.length === filteredUsuarios.length ? "Deseleccionar todos" : "Seleccionar todos"}
                  </Button>
                </div>

                {/* Lista de usuarios */}
                <ScrollArea className="flex-1 border rounded-lg">
                  {loadingUsers ? (
                    <div className="flex items-center justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-blue-600" />
                    </div>
                  ) : filteredUsuarios.length === 0 ? (
                    <div className="text-center py-8 text-slate-500">
                      <Users className="h-8 w-8 mx-auto mb-2 opacity-50" />
                      <p>No se encontraron usuarios</p>
                    </div>
                  ) : (
                    <div className="divide-y">
                      {filteredUsuarios.map(usuario => (
                        <div 
                          key={usuario.id}
                          className={`flex items-center gap-3 p-3 hover:bg-slate-50 dark:hover:bg-slate-700/50 cursor-pointer transition-colors ${
                            selectedUsers.includes(usuario.id) ? 'bg-blue-50 dark:bg-blue-900/20' : ''
                          }`}
                          onClick={() => handleToggleUser(usuario.id)}
                        >
                          <Checkbox 
                            checked={selectedUsers.includes(usuario.id)}
                            onCheckedChange={() => handleToggleUser(usuario.id)}
                          />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-800 dark:text-white truncate">
                              {usuario.name}
                            </p>
                            <p className="text-sm text-slate-500 truncate">
                              {usuario.email}
                            </p>
                          </div>
                          <div className="text-right">
                            <Badge variant="secondary" className="text-xs">
                              {usuario.departamento_nombre || usuario.role || "Sin depto."}
                            </Badge>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </ScrollArea>
              </div>
            )}

            <DialogFooter className="mt-4 pt-4 border-t">
              <Button variant="outline" onClick={() => setShowSendDialog(false)}>
                {sendResult ? "Cerrar" : "Cancelar"}
              </Button>
              {!sendResult && (
                <Button 
                  className="bg-green-600 hover:bg-green-700 text-white"
                  onClick={handleSendEmails}
                  disabled={sendingEmails || selectedUsers.length === 0}
                >
                  {sendingEmails ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <Send className="h-4 w-4 mr-2" />
                  )}
                  Enviar a {selectedUsers.length} usuario(s)
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Logo Configuration Dialog */}
        <Dialog open={showConfigDialog} onOpenChange={setShowConfigDialog}>
          <DialogContent className="max-w-lg dark:bg-slate-800">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Image className="h-5 w-5 text-blue-600" />
                Configurar Logo de Plantillas
              </DialogTitle>
              <DialogDescription>
                Configure el logo que aparecerá en los correos enviados
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              {/* Subir Logo con Drag and Drop */}
              <div>
                <Label>Subir Logo</Label>
                <div 
                  className={`mt-2 border-2 border-dashed rounded-lg p-6 text-center transition-all duration-200 cursor-pointer ${
                    isDraggingLogo 
                      ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 scale-[1.02]' 
                      : 'border-slate-300 dark:border-slate-600 hover:border-blue-400 hover:bg-slate-50 dark:hover:bg-slate-700/50'
                  }`}
                  onDragEnter={handleLogoDragEnter}
                  onDragLeave={handleLogoDragLeave}
                  onDragOver={handleLogoDragOver}
                  onDrop={handleLogoDrop}
                  onClick={() => !savingConfig && document.getElementById('logo-upload')?.click()}
                  data-testid="logo-dropzone"
                >
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                    onChange={handleLogoUpload}
                    className="hidden"
                    id="logo-upload"
                    disabled={savingConfig}
                  />
                  {savingConfig ? (
                    <div className="flex flex-col items-center">
                      <Loader2 className="h-10 w-10 animate-spin text-blue-600 mb-2" />
                      <span className="text-sm text-slate-600 dark:text-slate-300">Subiendo...</span>
                    </div>
                  ) : isDraggingLogo ? (
                    <div className="flex flex-col items-center">
                      <Upload className="h-10 w-10 text-blue-500 mb-2 animate-bounce" />
                      <span className="text-sm font-medium text-blue-600 dark:text-blue-400">
                        ¡Suelte el archivo aquí!
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <Upload className="h-10 w-10 text-slate-400 mb-2" />
                      <span className="text-sm font-medium text-slate-600 dark:text-slate-300">
                        Arrastre una imagen aquí o haga clic para seleccionar
                      </span>
                      <span className="text-xs text-slate-500 mt-1">
                        PNG, JPG, GIF, WebP o SVG (máx. 2MB)
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Separador */}
              <div className="flex items-center gap-3">
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-600"></div>
                <span className="text-xs text-slate-500">o ingrese una URL</span>
                <div className="flex-1 h-px bg-slate-200 dark:bg-slate-600"></div>
              </div>

              {/* URL Manual */}
              <div>
                <Label>URL del Logo (opcional)</Label>
                <Input
                  value={logoConfig.logo_url}
                  onChange={(e) => setLogoConfig({ ...logoConfig, logo_url: e.target.value })}
                  placeholder="https://ejemplo.com/logo.png"
                />
              </div>

              {/* Vista previa */}
              {logoConfig.logo_url && (
                <div className="border rounded-lg p-4 bg-slate-50 dark:bg-slate-700">
                  <p className="text-xs text-slate-500 mb-2">Vista previa:</p>
                  <div className="flex justify-center min-h-[64px] items-center">
                    <img 
                      src={logoConfig.logo_url} 
                      alt="Logo preview" 
                      className="max-h-16 max-w-[200px] object-contain"
                      onLoad={(e) => {
                        e.target.style.display = 'block';
                        const errorMsg = e.target.parentNode.querySelector('.logo-error');
                        if (errorMsg) errorMsg.style.display = 'none';
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const errorMsg = e.target.parentNode.querySelector('.logo-error');
                        if (errorMsg) errorMsg.style.display = 'block';
                      }}
                    />
                    <div className="logo-error hidden text-center">
                      <p className="text-xs text-red-500">No se pudo cargar la imagen</p>
                      <p className="text-xs text-slate-400 mt-1 break-all max-w-[250px]">{logoConfig.logo_url}</p>
                    </div>
                  </div>
                </div>
              )}

              <div>
                <Label>Color Primario</Label>
                <div className="flex gap-2">
                  <Input
                    type="color"
                    value={logoConfig.primary_color}
                    onChange={(e) => setLogoConfig({ ...logoConfig, primary_color: e.target.value })}
                    className="w-16 h-10 p-1 cursor-pointer"
                  />
                  <Input
                    value={logoConfig.primary_color}
                    onChange={(e) => setLogoConfig({ ...logoConfig, primary_color: e.target.value })}
                    placeholder="#0C314C"
                    className="flex-1"
                  />
                </div>
              </div>

              <div>
                <Label>Texto del Pie de Página (opcional)</Label>
                <Input
                  value={logoConfig.footer_text}
                  onChange={(e) => setLogoConfig({ ...logoConfig, footer_text: e.target.value })}
                  placeholder="© 2026 Mi Empresa. Todos los derechos reservados."
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 dark:bg-blue-900/20 dark:border-blue-800">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>Uso en plantillas:</strong> Use la variable <code className="bg-blue-100 px-1 rounded">{"{{sistema.logo_url}}"}</code> en sus plantillas para insertar el logo.
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setShowConfigDialog(false)}>
                Cancelar
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleSaveLogoConfig}
                disabled={savingConfig}
              >
                {savingConfig ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Guardar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
